import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ============================================
// TYPES
// ============================================

export type Language = 'en' | 'fr';

export interface Translations {
    // Common
    'common.save': string;
    'common.saveChanges': string;
    'common.cancel': string;
    'common.delete': string;
    'common.edit': string;
    'common.create': string;
    'common.add': string;
    'common.remove': string;
    'common.search': string;
    'common.filter': string;
    'common.loading': string;
    'common.noData': string;
    'common.error': string;
    'common.success': string;
    'common.confirm': string;
    'common.close': string;
    'common.yes': string;
    'common.no': string;
    'common.none': string;
    'common.all': string;
    'common.clear': string;
    'common.actions': string;
    'common.settings': string;
    'common.name': string;
    'common.email': string;
    'common.password': string;
    'common.submit': string;
    'common.back': string;
    'common.next': string;
    'common.previous': string;
    'common.today': string;
    'common.tomorrow': string;
    'common.yesterday': string;
    'common.overdue': string;
    'common.unassigned': string;
    'common.viewAll': string;

    // Navigation
    'nav.home': string;
    'nav.dashboard': string;
    'nav.projects': string;
    'nav.myTasks': string;
    'nav.notifications': string;
    'nav.settings': string;
    'nav.signOut': string;
    'nav.favorites': string;

    // Auth
    'auth.login': string;
    'auth.register': string;
    'auth.signIn': string;
    'auth.signUp': string;
    'auth.logout': string;
    'auth.forgotPassword': string;
    'auth.resetPassword': string;
    'auth.forgotPasswordTitle': string;
    'auth.forgotPasswordDescription': string;
    'auth.sendResetLink': string;
    'auth.sendingResetLink': string;
    'auth.resetEmailSent': string;
    'auth.backToLogin': string;
    'auth.resetPasswordDescription': string;
    'auth.confirmNewPassword': string;
    'auth.resettingPassword': string;
    'auth.passwordResetSuccess': string;
    'auth.checkYourEmailTitle': string;
    'auth.checkYourEmailBody': string;
    'auth.checkYourEmailHint': string;
    'auth.verifyingEmail': string;
    'auth.emailVerifiedTitle': string;
    'auth.emailVerifiedBody': string;
    'auth.goToDashboard': string;
    'auth.verifyEmailFailedTitle': string;
    'auth.verifyEmailFailedBody': string;
    'auth.resendVerification': string;
    'auth.resendVerificationHint': string;
    'auth.verificationResentTitle': string;
    'auth.verificationResentBody': string;
    'auth.loginUnverifiedBody': string;
    'auth.backToSignIn': string;
    'auth.sending': string;
    'auth.invalidResetLink': string;
    'auth.rememberMe': string;
    'auth.noAccount': string;
    'auth.hasAccount': string;
    'auth.haveAccount': string;
    'auth.emailPlaceholder': string;
    'auth.passwordPlaceholder': string;
    'auth.namePlaceholder': string;
    'auth.confirmPassword': string;
    'auth.currentPassword': string;
    'auth.newPassword': string;
    'auth.invalidCredentials': string;
    'auth.emailRequired': string;
    'auth.passwordRequired': string;
    'auth.nameRequired': string;
    'auth.passwordMinLength': string;
    'auth.passwordsDoNotMatch': string;
    'auth.welcomeBack': string;
    'auth.createAccount': string;
    'auth.createAccountBtn': string;
    'auth.creatingAccount': string;
    'auth.getStarted': string;
    'auth.signInToAccount': string;
    'auth.signingIn': string;
    'auth.email': string;
    'auth.password': string;
    'auth.name': string;
    'auth.password8Chars': string;
    'auth.passwordUppercase': string;
    'auth.passwordLowercase': string;
    'auth.passwordNumber': string;
    // OAuth
    'auth.continueWithGoogle': string;
    'auth.continueWithDiscord': string;
    'auth.orContinueWith': string;
    'auth.oauthDenied': string;
    'auth.oauthInvalidRequest': string;
    'auth.oauthInvalidState': string;
    'auth.oauthTokenFailed': string;
    'auth.oauthUserFetchFailed': string;
    'auth.oauthEmailRequired': string;
    'auth.oauthServerError': string;

    // Projects
    'projects.title': string;
    'projects.createProject': string;
    'projects.newProject': string;
    'projects.editProject': string;
    'projects.deleteProject': string;
    'projects.archiveProject': string;
    'projects.unarchiveProject': string;
    'projects.leaveProject': string;
    'projects.projectName': string;
    'projects.projectDescription': string;
    'projects.projectColor': string;
    'projects.noProjects': string;
    'projects.searchProjects': string;
    'projects.showArchived': string;
    'projects.showActive': string;
    'projects.hideArchived': string;
    'projects.members': string;
    'projects.tasks': string;
    'projects.invite': string;
    'projects.inviteMembers': string;
    'projects.inviteSent': string;
    'projects.projectSettings': string;
    'projects.dangerZone': string;
    'projects.deleteWarning': string;
    'projects.owner': string;
    'projects.admin': string;
    'projects.member': string;
    'projects.viewer': string;
    'projects.manageProjects': string;
    'projects.noProjectsFound': string;
    'projects.noArchivedProjects': string;
    'projects.tryDifferentSearch': string;
    'projects.archiveToSeeHere': string;
    'projects.createFirstProject': string;
    'projects.deleteConfirmation': string;
    'projects.leaveConfirmation': string;
    'projects.currentProject': string;
    'projects.manageTeam': string;
    'projects.general': string;
    'projects.teamMembers': string;
    'projects.customization': string;
    'projects.projectIcon': string;
    'projects.useEmoji': string;
    'projects.useImage': string;
    'projects.selectEmoji': string;
    'projects.uploadImage': string;
    'projects.changeImage': string;
    'projects.removeImage': string;
    'projects.imageHint': string;
    'projects.inviteEmail': string;
    'projects.role': string;
    'projects.expires': string;
    'projects.expiresInDays': string;
    'projects.day': string;
    'projects.days': string;
    'projects.pendingInvitations': string;
    'projects.cancelInvitation': string;
    'projects.copyLink': string;
    'projects.linkCopied': string;
    'projects.removeMember': string;
    'projects.changeRole': string;
    'projects.noMembers': string;
    'projects.inviteDescription': string;
    'projects.invalidFileType': string;
    'projects.fileTooLarge': string;

    // Project aliases (both 'project.' and 'projects.' work)
    'project.createProject': string;
    'project.newProject': string;
    'project.manageProjects': string;
    'project.searchProjects': string;
    'project.showActive': string;
    'project.showArchived': string;
    'project.noProjectsFound': string;
    'project.noArchivedProjects': string;
    'project.noProjects': string;
    'project.tryDifferentSearch': string;
    'project.archiveToSeeHere': string;
    'project.createFirstProject': string;
    'project.deleteProject': string;
    'project.leaveProject': string;
    'project.deleteConfirmation': string;
    'project.leaveConfirmation': string;

    // Tasks
    'tasks.title': string;
    'tasks.myTasks': string;
    'tasks.createTask': string;
    'tasks.newTask': string;
    'tasks.editTask': string;
    'tasks.deleteTask': string;
    'tasks.taskName': string;
    'tasks.taskDescription': string;
    'tasks.noTasks': string;
    'tasks.noTasksDescription': string;
    'tasks.addTask': string;
    'tasks.addSubtask': string;
    'tasks.taskDetails': string;
    'tasks.resizePanel': string;
    'tasks.subtasks': string;
    // t14: Gantt dependencies on the side panel
    'tasks.dependsOn': string;
    'tasks.dependsOnEmpty': string;
    'tasks.dependsOnNoMatches': string;
    'tasks.dependsOnSearchPlaceholder': string;
    'tasks.assignees': string;
    'tasks.assignTo': string;
    'tasks.selectAssignees': string;
    'tasks.dueDate': string;
    'tasks.createdAt': string;
    'tasks.setDueDate': string;
    // t35 (R8): side panel custom-fields section
    'tasks.customFieldsSection': string;
    'tasks.customFieldAdd': string;
    'tasks.customFieldNone': string;
    // t14: auto-managed start/end dates surfaced as read-only labels
    // in the task-detail panel.
    'tasks.startDate': string;
    'tasks.endDate': string;
    'tasks.notSet': string;
    'tasks.clearDate': string;
    'tasks.status': string;
    'tasks.noMatchingTasks': string;
    'tasks.tryAdjustingFilters': string;
    'tasks.getStartedCreatingTask': string;
    'tasks.createFirstTask': string;
    'tasks.assign': string;
    'tasks.customFields': string;
    'tasks.columns': string;
    'tasks.noTasksFound': string;
    'tasks.enterTitle': string;
    'tasks.enterSubtaskTitle': string;
    'tasks.clearValue': string;
    'tasks.editIndividually': string;
    'tasks.toggleOptions': string;
    'tasks.checked': string;
    'tasks.unchecked': string;
    'tasks.deleteTaskAction': string;
    'tasks.loadingSubtasks': string;
    'tasks.editTitle': string;

    // Task aliases
    'task.today': string;
    'task.tomorrow': string;
    'task.overdue': string;
    'task.noTasks': string;
    'task.noTasksDescription': string;
    'tasks.priority': string;
    'tasks.comments': string;
    'tasks.attachments': string;
    'tasks.task': string;
    'tasks.selected': string;
    'tasks.setStatus': string;
    'tasks.setPriority': string;

    // Task Status
    'status.todo': string;
    'status.inProgress': string;
    'status.inReview': string;
    'status.done': string;
    'status.cancelled': string;

    // Task Priority
    'priority.none': string;
    'priority.low': string;
    'priority.medium': string;
    'priority.high': string;
    'priority.urgent': string;

    // Views
    'views.savedViews': string;
    'views.saveView': string;
    'views.editView': string;
    'views.editViewTitle': string;
    'views.deleteView': string;
    'views.duplicateView': string;
    'views.setDefault': string;
    'views.setAsDefault': string;
    'views.shareWithTeam': string;
    'views.viewName': string;
    'views.viewNamePlaceholder': string;
    'views.saveViewDescription': string;
    'views.editViewDescription': string;
    'views.allTasks': string;
    'views.noViews': string;
    'views.isDefault': string;
    'views.isShared': string;
    'views.saveCurrentView': string;
    'views.favorites': string;
    'views.personal': string;
    'views.shared': string;
    'views.noFavorites': string;
    'views.noPersonalViews': string;
    'views.noSharedViews': string;
    'views.addToFavorites': string;
    'views.removeFromFavorites': string;
    'views.isFavorite': string;

    // View type toggle (Table / Kanban / Calendar / Gantt)
    'viewType.table': string;
    'viewType.kanban': string;
    'viewType.calendar': string;
    'viewType.gantt': string;
    'viewType.document': string;
    'viewType.toggleAriaLabel': string;

    // R14: Document mode (Google-Docs-like rich text editor)
    'document.defaultTabName': string;
    'document.newTab': string;
    'document.closeTab': string;
    'document.tabAriaLabel': string;
    'document.tabsAriaLabel': string;
    'document.emptyTitle': string;
    'document.emptyBody': string;
    'document.emptyCreateCta': string;
    // R20 (t62): "Read-only" badge shown to VIEWER-role users.
    'document.readOnlyBadge': string;
    'document.loading': string;
    'document.connecting': string;
    'document.connected': string;
    'document.disconnected': string;
    'document.saveStatus.saving': string;
    'document.saveStatus.saved': string;
    'document.saveStatus.error': string;
    // R26: honest autosave state — bytes pending, no request in flight
    // yet (idle debounce / 10 s max-wait still counting).
    'document.saveStatus.dirty': string;
    /** R26: toast body shown once when a snapshot upload fails. */
    'document.saveStatus.errorHint': string;
    'document.lastSavedAt': string;
    'document.collab.peers': string;
    'document.collab.onePeer': string;
    'document.collab.zeroPeers': string;
    'document.toolbar.bold': string;
    'document.toolbar.italic': string;
    'document.toolbar.underline': string;
    'document.toolbar.h1': string;
    'document.toolbar.h2': string;
    'document.toolbar.h3': string;
    'document.toolbar.bulletList': string;
    'document.toolbar.orderedList': string;
    'document.toolbar.blockquote': string;
    'document.toolbar.paragraph': string;
    'document.toolbar.fontFamily': string;
    'document.toolbar.fontSize': string;
    'document.toolbar.fontFamily.sans': string;
    'document.toolbar.fontFamily.arial': string;
    'document.toolbar.fontFamily.serif': string;
    'document.toolbar.fontFamily.times': string;
    'document.toolbar.fontFamily.verdana': string;
    'document.toolbar.fontFamily.courier': string;
    'document.toolbar.fontFamily.mono': string;
    'document.toolbar.fontFamily.comic': string;
    'document.toolbar.fontFamily.default': string;
    'document.toolbar.fontSize.small': string;
    'document.toolbar.fontSize.medium': string;
    'document.toolbar.fontSize.large': string;
    'document.toolbar.fontSize.xs': string;
    'document.toolbar.fontSize.xl': string;
    'document.toolbar.fontSize.xxl': string;
    'document.toolbar.fontSize.default': string;
    'document.toolbar.fontSizeCustomHint': string;
    'document.toolbar.color': string;
    'document.toolbar.colorCustom': string;
    'document.toolbar.colorClear': string;
    'document.toolbar.tocTitle': string;
    'document.toolbar.tocEmpty': string;
    'document.toolbar.tocUntitled': string;
    'document.toolbar.insertTable': string;
    'document.toolbar.addRowBefore': string;
    'document.toolbar.addColumnBefore': string;
    'document.toolbar.mergeCells': string;
    'document.toolbar.splitCell': string;
    'document.toolbar.toggleHeaderRow': string;
    'document.toolbar.toggleHeaderColumn': string;
    'document.toolbar.addRowAfter': string;
    'document.toolbar.addColumnAfter': string;
    'document.toolbar.deleteRow': string;
    'document.toolbar.deleteColumn': string;
    'document.toolbar.deleteTable': string;
    'document.toolbar.undo': string;
    'document.toolbar.redo': string;
    // R20: text alignment (left / center / right / justify)
    'document.toolbar.alignLeft': string;
    'document.toolbar.alignCenter': string;
    'document.toolbar.alignRight': string;
    'document.toolbar.alignJustify': string;
    'document.toolbar.alignLeftHint': string;
    'document.toolbar.alignCenterHint': string;
    'document.toolbar.alignRightHint': string;
    'document.toolbar.alignJustifyHint': string;
    // R18 (t59): export-to-PDF / DOCX button in the toolbar.
    'document.exportMenu': string;
    'document.exportPdf': string;
    'document.exportDocx': string;
    'document.exportTitle': string;
    'document.editorPlaceholder': string;
    'document.deleteViewConfirm': string;
    // R16: hint shown alongside the editor placeholder so the
    // user discovers the `3x4` → table shortcut. We deliberately
    // do NOT render this as a separate banner — the placeholder
    // already serves the same role and the editor is full-bleed.
    'document.tableHint': string;
    // R14: @-mention picker
    'document.mention.loading': string;
    'document.mention.empty': string;
    'document.mention.pickerAriaLabel': string;
    'document.mention.task': string;
    // R16: people + search bar + grouped sections in the mention picker
    'document.mention.person': string;
    'document.mention.searchPlaceholder': string;
    'document.mention.searchAriaLabel': string;
    'document.mention.section.tasks': string;
    'document.mention.section.people': string;
    'document.mention.notified': string;
    'document.mention.notifiedTitle': string;
    'document.mention.emptyAfterSearch': string;
    // R25: Bug A server-search status + Bug B person-mention popover
    'document.mention.searchUnavailable': string;
    'document.mention.loadMore': string;
    'document.mention.personTasksTitle': string;
    'document.mention.personTasksEmpty': string;
    'document.mention.personTasksError': string;

    // Gantt view (t17)
    'gantt.comingSoonTitle': string;
    'gantt.comingSoonBody': string;
    'gantt.activeViewLabel': string;
    'gantt.previous': string;
    'gantt.next': string;
    'gantt.settings': string;
    'gantt.timeScaleLabel': string;
    'gantt.timeScale.day': string;
    'gantt.timeScale.week': string;
    'gantt.timeScale.month': string;
    'gantt.timeScale.auto': string;
    'gantt.groupByLabel': string;
    'gantt.groupBy.status': string;
    'gantt.groupBy.assignee': string;
    'gantt.groupBy.none': string;
    'gantt.showSprintsLabel': string;
    'gantt.showSprintsHint': string;
    // R3 / t17: show tasks without startDate/endDate (default off).
    'gantt.showDatelessLabel': string;
    'gantt.showDatelessHint': string;
    // R3 / t17: subtask expand/collapse chevron button on each row.
    'gantt.expandSubtasks': string;
    'gantt.collapseSubtasks': string;
    // R6 / t31: right-click → single-option cursor-arrow
    // pick-target flow.
    'gantt.depMenuSingle': string;
    'gantt.depPickHint': string;
    'gantt.depPickCancelHint': string;
    'gantt.settingsTitle': string;
    'gantt.settingsHint': string;
    // t14: Gantt dependency context-menu + dialogs
    'gantt.addDependency': string;
    'gantt.removeDependency': string;
    'gantt.addDependencyTitle': string;
    'gantt.addDependencyDescription': string;
    'gantt.removeDependencyTitle': string;
    'gantt.removeDependencyDescription': string;
    'gantt.dependencyDirection': string;
    'gantt.dependencyAsSuccessor': string;
    'gantt.dependencyAsPredecessor': string;
    'gantt.dependencyTarget': string;
    'gantt.dependencySearchPlaceholder': string;
    'gantt.dependencyType': string;
    'gantt.dependencyTypeFS': string;
    'gantt.dependencyTypeSS': string;
    'gantt.dependencyTypeFF': string;
    'gantt.dependencyTypeSF': string;
    'gantt.emptyTitle': string;
    'gantt.emptyBody': string;
    'gantt.errorUpdateDates': string;
    'gantt.loadError': string;
    'gantt.requiresSprint': string;
    'gantt.bar.handleAria': string;
    'gantt.bar.moveAria': string;
    'gantt.bar.resizeEndAria': string;
    'gantt.bar.resizeStartAria': string;
    'gantt.bar.assignedTo': string;
    'gantt.status.TODO': string;
    'gantt.status.IN_PROGRESS': string;
    'gantt.status.IN_REVIEW': string;
    'gantt.status.DONE': string;
    'gantt.status.CANCELLED': string;
    'gantt.unassigned': string;
    'gantt.filtersLabel': string;

    // Calendar view (t16)
    'calendar.comingSoonTitle': string;
    'calendar.comingSoonBody': string;
    'calendar.activeViewLabel': string;
    'calendar.previous': string;
    'calendar.next': string;
    'calendar.settings': string;
    'calendar.viewModeLabel': string;
    'calendar.dateSourceLabel': string;
    'calendar.colorByLabel': string;
    'calendar.viewMode.month': string;
    'calendar.viewMode.week': string;
    'calendar.viewMode.agenda': string;
    'calendar.dateSource.dueDate': string;
    'calendar.dateSource.startDate': string;
    'calendar.dateSource.endDate': string;
    'calendar.dateSource.sprintStart': string;
    'calendar.dateSource.sprintEnd': string;
    'calendar.colorBy.status': string;
    'calendar.colorBy.priority': string;
    'calendar.colorBy.assignee': string;
    'calendar.requiresSprint': string;
    'calendar.weekday.0': string;
    'calendar.weekday.1': string;
    'calendar.weekday.2': string;
    'calendar.weekday.3': string;
    'calendar.weekday.4': string;
    'calendar.weekday.5': string;
    'calendar.weekday.6': string;
    'calendar.more': string;
    'calendar.agendaEmptyTitle': string;
    'calendar.agendaEmptyBody': string;
    'calendar.settingsTitle': string;
    'calendar.settingsHint': string;
    'calendar.errorUpdateDate': string;
    'calendar.loadError': string;
    // t18: right-click context menu strings
    'calendar.contextMenu.createTaskAt': string;
    'calendar.contextMenu.openTask': string;
    'calendar.contextMenu.deleteTask': string;
    // T16 R3: settings-panel filter-rule editor
    'calendar.filterStatusLabel': string;
    'calendar.filterStatusHint': string;
    'calendar.filterClear': string;
    'calendar.subtasksLabel': string;
    'calendar.subtasksEmpty': string;
    // T21 R4: "All tasks" date source + Periods section
    'calendar.dateSource.all': string;
    'calendar.periodsLabel': string;
    'calendar.periodsHint': string;
    'calendar.periodAdd': string;
    'calendar.periodRemove': string;
    'calendar.periodClearAll': string;
    'calendar.periodNamePlaceholder': string;
    'calendar.periodStart': string;
    'calendar.periodEnd': string;
    'calendar.periodColor': string;

    // Kanban subtask popover (t15)
    'kanban.subtaskButton': string;
    'kanban.subtaskPopoverTitle': string;
    'kanban.subtaskEmpty': string;

    // Kanban settings cog popover (t15)
    'kanban.settings': string;
    'kanban.settingsTitle': string;
    'kanban.settingsHint': string;
    'kanban.cardFieldsSection': string;
    'kanban.columnVisibilitySection': string;

    // Kanban-specific UI strings
    'kanban.cardFieldsTitle': string;
    'kanban.cardFieldsHint': string;
    'kanban.noCardFieldsAvailable': string;
    'kanban.columnGrouping': string;
    'kanban.columnGroupingHint': string;
    'kanban.dropHint': string;
    'kanban.addColumn': string;
    'kanban.addColumnTitle': string;
    'kanban.addColumnDescription': string;
    'kanban.columnName': string;
    'kanban.columnNamePlaceholder': string;
    'kanban.columnColor': string;
    'kanban.columnFilterRules': string;
    'kanban.addRule': string;
    'kanban.columns': string;
    'kanban.columnsVisibility': string;
    'kanban.columnsVisibilityHint': string;
    'kanban.noColumns': string;
    'kanban.noColumnsTitle': string;
    'kanban.noColumnsDescription': string;
    'kanban.createFirstColumn': string;
    'kanban.cardFields': string;
    'kanban.columnCreated': string;
    'kanban.columnDeleted': string;
    'kanban.errorCreateColumn': string;
    'kanban.errorDeleteColumn': string;
    'kanban.errorUpdateColumn': string;
    'kanban.errorReorderColumns': string;
    'kanban.cannotDropHere': string;
    'kanban.cannotDropHereDescription': string;
    'kanban.editColumnTitle': string;
    'kanban.editColumnDescription': string;
    'kanban.columnUpdated': string;
    'kanban.contextMenuEdit': string;
    'kanban.contextMenuHide': string;
    'kanban.contextMenuDelete': string;
    'kanban.fieldTag.status': string;
    'kanban.fieldTag.priority': string;
    'kanban.fieldTag.assignee': string;
    'kanban.fieldTag.customField': string;
    'kanban.fieldTag.other': string;
    'kanban.operator.eq': string;
    'kanban.operator.neq': string;
    'kanban.operator.in': string;
    'kanban.operator.notIn': string;
    'kanban.operator.isEmpty': string;
    'kanban.operator.isNotEmpty': string;
    'kanban.operator.contains': string;
    'kanban.field.status': string;
    'kanban.field.priority': string;
    'kanban.field.assignee': string;
    'kanban.field.customField': string;
    'kanban.statusValue.TODO': string;
    'kanban.statusValue.IN_PROGRESS': string;
    'kanban.statusValue.IN_REVIEW': string;
    'kanban.statusValue.DONE': string;
    'kanban.statusValue.CANCELLED': string;
    'kanban.priorityValue.NONE': string;
    'kanban.priorityValue.LOW': string;
    'kanban.priorityValue.MEDIUM': string;
    'kanban.priorityValue.HIGH': string;
    'kanban.priorityValue.URGENT': string;

    // Filters
    'filters.filters': string;
    'filters.clearFilters': string;
    'filters.clearAll': string;
    'filters.filterByStatus': string;
    'filters.filterByPriority': string;
    'filters.filterByAssignee': string;
    'filters.filterByDueDate': string;
    'filters.activeFilters': string;
    'filters.status': string;
    'filters.priority': string;
    'filters.assignee': string;
    'filters.allAssignees': string;
    'filters.filter': string;
    'filters.saveAsView': string;
    'filters.saveViewChanges': string;
    'filters.addField': string;
    'filters.searchTasks': string;

    // t43 (R13): custom-field filter operator + placeholder keys.
    'filter.operator.contains': string;
    'filter.operator.notContains': string;
    'filter.operator.equals': string;
    'filter.operator.notEquals': string;
    'filter.operator.startsWith': string;
    'filter.operator.endsWith': string;
    'filter.operator.lt': string;
    'filter.operator.lte': string;
    'filter.operator.gt': string;
    'filter.operator.gte': string;
    'filter.operator.between': string;
    'filter.operator.before': string;
    'filter.operator.after': string;
    'filter.operator.is': string;
    'filter.operator.isNot': string;
    'filter.operator.isAnyOf': string;
    'filter.operator.isNoneOf': string;
    'filter.operator.containsAll': string;
    'filter.operator.containsAny': string;
    'filter.operator.isEmpty': string;
    'filter.operator.isNotEmpty': string;
    'filter.operator.isTrue': string;
    'filter.operator.isFalse': string;
    'filter.valuePlaceholder.text': string;
    'filter.valuePlaceholder.number': string;
    'filter.valuePlaceholder.select': string;
    'filter.valuePlaceholder.selectMulti': string;
    'filter.valuePlaceholder.selectPerson': string;
    'filter.selected': string;
    'filter.addFilter': string;
    'filter.filterByField': string;
    // t45 (R14): filter dropdown grouping
    'filter.group.text': string;
    'filter.group.number': string;
    'filter.group.date': string;
    'filter.group.category': string;
    'filter.group.person': string;

    // Comments
    'comments.addComment': string;
    'comments.editComment': string;
    'comments.deleteComment': string;
    'comments.deleteTitle': string;
    'comments.deleteConfirm': string;
    'comments.noComments': string;
    'comments.beFirstToComment': string;
    'comments.commentPlaceholder': string;
    'comments.editPlaceholder': string;
    'comments.edited': string;
    'comments.submitHint': string;
    'comments.isTyping': string;
    'comments.areTyping': string;
    'comments.andOthersTyping': string;
    'comments.save': string;
    'comments.comment': string;
    'comments.failedToLoad': string;
    'comments.mentionCount': string;
    'comments.mentionHint': string;

    // Attachments
    'attachments.addAttachment': string;
    'attachments.uploadFile': string;
    'attachments.upload': string;
    'attachments.noAttachments': string;
    'attachments.noAttachmentsHint': string;
    'attachments.download': string;
    'attachments.deleteAttachment': string;
    'attachments.deleteTitle': string;
    'attachments.deleteConfirm': string;
    'attachments.uploaded': string;
    'attachments.failedToLoad': string;
    'attachments.fileTooLarge': string;
    'attachments.openInNewTab': string;

    // Notifications
    'notifications.title': string;
    'notifications.markAllRead': string;
    'notifications.noNotifications': string;
    'notifications.viewInvitation': string;
    'notifications.taskAssigned': string;
    'notifications.taskAssignedDesc': string;
    'notifications.taskUpdated': string;
    'notifications.taskUpdatedDesc': string;
    'notifications.commentAdded': string;
    'notifications.commentAddedDesc': string;
    'notifications.attachmentAdded': string;
    'notifications.attachmentAddedDesc': string;
    'notifications.mentioned': string;
    'notifications.mentionedDesc': string;
    'notifications.projectInvite': string;
    'notifications.projectInviteDesc': string;
    'notifications.dueDateReminder': string;
    'notifications.dueDateReminderDesc': string;
    'notifications.taskOverdue': string;
    'notifications.taskOverdueDesc': string;

    // Settings
    'settings.title': string;
    'settings.profile': string;
    'settings.profileInfo': string;
    'settings.profilePicture': string;
    'settings.uploadPicture': string;
    'settings.removePicture': string;
    'settings.changePassword': string;
    'settings.updatePassword': string;
    'settings.language': string;
    'settings.language.title': string;
    'settings.language.description': string;
    'settings.language.select': string;
    'settings.selectLanguage': string;
    'settings.notificationSettings': string;
    'settings.discordIntegration': string;
    'settings.discordIntegrationDesc': string;
    'settings.connectDiscord': string;
    'settings.disconnectDiscord': string;
    'settings.discordConnected': string;
    'settings.discordReady': string;
    'settings.discordReadyDescription': string;
    'settings.discordNeedsReconnect': string;
    'settings.reconnectDiscord': string;
    'settings.discordLinkStepInstall': string;
    'settings.discordLinkStepCommand': string;
    'settings.discordCommandHint': string;
    'settings.installDiscordApp': string;
    'settings.copyCode': string;
    'settings.codeCopied': string;
    'settings.inAppNotifications': string;
    'settings.discordNotifications': string;
    'settings.emailFallbackNotifications': string;
    'settings.emailFallbackDescription': string;
    'settings.reminderTiming': string;
    'settings.reminderTimingDesc': string;
    'settings.before1h': string;
    'settings.before24h': string;
    'settings.before48h': string;
    'settings.after24h': string;
    'settings.after48h': string;
    // Theme settings
    'settings.theme': string;
    'settings.themeTitle': string;
    'settings.themeDescription': string;
    'settings.interactiveEffects': string;
    'settings.interactiveEffectsDesc': string;

    // Dashboard
    'dashboard.welcome': string;
    'dashboard.assignedTasks': string;
    'dashboard.dueToday': string;
    'dashboard.completedThisWeek': string;
    'dashboard.recentTasks': string;
    'dashboard.quickActions': string;
    'dashboard.createNewProject': string;

    // Errors
    'error.somethingWentWrong': string;
    'error.notFound': string;
    'error.unauthorized': string;
    'error.forbidden': string;
    'error.networkError': string;
    'error.tryAgain': string;
    'error.projectNotFoundDesc': string;

    // Success messages
    'success.saved': string;
    'success.deleted': string;
    'success.created': string;
    'success.updated': string;
    'success.copied': string;

    // Confirmation dialogs
    'confirm.deleteTask': string;
    'confirm.deleteProject': string;
    'confirm.deleteComment': string;
    'confirm.deleteAttachment': string;
    'confirm.deleteView': string;
    'confirm.leaveProject': string;
    'confirm.cannotUndo': string;
    'confirm.deleteCustomField': string;

    // Custom fields
    'customFields.title': string;
    'customFields.addField': string;
    'customFields.editField': string;
    'customFields.deleteField': string;
    'customFields.fieldName': string;
    'customFields.fieldType': string;
    'customFields.required': string;
    'customFields.options': string;
    'customFields.addOption': string;
    'customFields.text': string;
    'customFields.number': string;
    'customFields.date': string;
    'customFields.select': string;
    'customFields.multiSelect': string;
    'customFields.checkbox': string;
    'customFields.url': string;
    'customFields.email': string;
    'customFields.person': string;
    'customFields.sprint': string;
    // Sprint option editor — per-sprint rows have name + start + end.
    'customFields.sprintOptionName': string;
    'customFields.sprintOptionStart': string;
    'customFields.sprintOptionEnd': string;
    'customFields.sprintAddOption': string;
    'customFields.sprintNoOptions': string;

    // Project Settings
    'projectSettings.projectDetails': string;
    // Invite Links (R14 t46)
    'inviteLink.sectionTitle': string;
    'inviteLink.sectionDescription': string;
    'inviteLink.create': string;
    'inviteLink.createFirst': string;
    'inviteLink.createTitle': string;
    'inviteLink.createDescription': string;
    'inviteLink.role': string;
    'inviteLink.role.MEMBER': string;
    'inviteLink.role.MEMBER.description': string;
    'inviteLink.role.VIEWER': string;
    'inviteLink.role.VIEWER.description': string;
    'inviteLink.hasExpiry': string;
    'inviteLink.expiresAt': string;
    'inviteLink.empty': string;
    'inviteLink.copy': string;
    'inviteLink.copied': string;
    'inviteLink.copyFailed': string;
    'inviteLink.revoke': string;
    'inviteLink.revoked': string;
    'inviteLink.created': string;
    'inviteLink.createdBy': string;
    'inviteLink.status.active': string;
    'inviteLink.status.revoked': string;
    'inviteLink.status.expired': string;
    'inviteLink.title': string;
    'inviteLink.subtitle': string;
    'inviteLink.invitedBy': string;
    'inviteLink.accept': string;
    'inviteLink.decline': string;
    'inviteLink.authenticatedNotice': string;
    'inviteLink.acceptSuccess': string;
    'inviteLink.acceptSuccessDescription': string;
    'inviteLink.acceptFailed': string;
    'inviteLink.invalid': string;
    'inviteLink.invalidDescription': string;
    'inviteLink.backToProjects': string;
    'projectSettings.projectName': string;
    'projectSettings.description': string;
    'projectSettings.projectIcon': string;
    'projectSettings.emoji': string;
    'projectSettings.image': string;
    'projectSettings.chooseEmoji': string;
    'projectSettings.uploadImage': string;
    'projectSettings.maxSize': string;
    'projectSettings.color': string;
    'projectSettings.processingImage': string;
    'projectSettings.saveChanges': string;
    'projectSettings.dangerZone': string;
    'projectSettings.dangerZoneDescription': string;
    'projectSettings.teamMembers': string;
    'projectSettings.pendingInvitations': string;
    'projectSettings.inviteTeamMember': string;
    'projectSettings.inviteDescription': string;
    'projectSettings.joinLinkTitle': string;
    'projectSettings.joinLinkDescription': string;
    'projectSettings.createJoinLink': string;
    'projectSettings.regenerateJoinLink': string;
    'projectSettings.disableJoinLink': string;
    'projectSettings.joinLinkWarning': string;
    'projectSettings.integrations': string;
    'projectSettings.discordWebhookTitle': string;
    'projectSettings.discordWebhookDescription': string;
    'projectSettings.discordWebhookUrl': string;
    'projectSettings.discordWebhookHint': string;
    'projectSettings.connectWebhook': string;
    'projectSettings.testWebhook': string;
    'projectSettings.disconnectWebhook': string;
    'projectSettings.webhookConnected': string;
    'projectSettings.webhookChannel': string;
    'projectSettings.webhookSaved': string;
    'projectSettings.webhookRemoved': string;
    'projectSettings.webhookTestSent': string;
    'projectSettings.data': string;
    'projectSettings.transferTitle': string;
    'projectSettings.transferDescription': string;
    'projectSettings.exportTitle': string;
    'projectSettings.exportDescription': string;
    'projectSettings.exportButton': string;
    'projectSettings.exporting': string;
    'projectSettings.importTitle': string;
    'projectSettings.importDescription': string;
    'projectSettings.importButton': string;
    'projectSettings.previewingImport': string;
    'projectSettings.importSafetyHint': string;
    'projectSettings.importPreviewTitle': string;
    'projectSettings.importPreviewDescription': string;
    'projectSettings.tasksToCreate': string;
    'projectSettings.tasksToUpdate': string;
    'projectSettings.tasksToDelete': string;
    'projectSettings.fieldsToCreate': string;
    'projectSettings.fieldsToUpdate': string;
    'projectSettings.optionsToAdd': string;
    'projectSettings.importWarnings': string;
    'projectSettings.confirmImport': string;
    'projectSettings.importing': string;
    'projectSettings.importSuccess': string;
    'projectSettings.importSuccessDescription': string;
    'projectSettings.invalidImportFile': string;
    'projectSettings.importFileTooLarge': string;
    'projectSettings.emailAddress': string;
    'projectSettings.emailPlaceholder': string;
    'projectSettings.role': string;
    'projectSettings.roleAdminDesc': string;
    'projectSettings.roleMemberDesc': string;
    'projectSettings.roleViewerDesc': string;
    'projectSettings.sendInvitation': string;
    'projectSettings.copyLink': string;
    'projectSettings.youIndicator': string;
    'projectSettings.projectNotFound': string;
    'projectSettings.backToProjects': string;
    'projectSettings.deleteProjectTitle': string;
    'projectSettings.deleteProjectDescription': string;

    // Toast messages
    'toast.invalidFileType': string;
    'toast.fileTooLarge': string;
    'toast.uploadFailed': string;
    'toast.settingsSaved': string;
    'toast.settingsSavedDesc': string;
    'toast.invitationSent': string;
    'toast.invitationSentDesc': string;
    'toast.projectDeleted': string;
    'toast.projectDeletedDesc': string;
    'toast.roleUpdated': string;
    'toast.roleUpdatedDesc': string;
    'toast.memberRemoved': string;
    'toast.memberRemovedDesc': string;
    'toast.invitationCancelled': string;
    'toast.invitationCancelledDesc': string;
    'toast.linkCopied': string;
    'toast.linkCopiedDesc': string;

    // Table columns
    'table.selection': string;
    'table.task': string;
    'table.actions': string;
    'table.horizontalScroll': string;
    'tasks.paginationShowing': string;
    'tasks.paginationOf': string;
    'tasks.loadMore': string;

    // Task Dialog
    'taskDialog.createTitle': string;
    'taskDialog.editTitle': string;
    'taskDialog.createDescription': string;
    'taskDialog.editDescription': string;
    'taskDialog.title': string;
    'taskDialog.titleRequired': string;
    'taskDialog.titlePlaceholder': string;
    'taskDialog.description': string;
    'taskDialog.descriptionPlaceholder': string;
    'taskDialog.selectStatus': string;
    'taskDialog.selectPriority': string;
    'taskDialog.noTeamMembers': string;
    'taskDialog.saveChanges': string;
    'taskDialog.createTask': string;
    'taskDialog.owner': string;
    'taskDialog.customFields': string;
    'taskDialog.selectPlaceholder': string;

    // Create Project Dialog
    'createProject.title': string;
    'createProject.description': string;
    'createProject.projectName': string;
    'createProject.projectNamePlaceholder': string;
    'createProject.projectDescription': string;
    'createProject.projectDescriptionPlaceholder': string;
    'createProject.descriptionOptional': string;
    'createProject.projectIcon': string;
    'createProject.emoji': string;
    'createProject.image': string;
    'createProject.chooseEmoji': string;
    'createProject.uploadImage': string;
    'createProject.maxSizeHint': string;
    'createProject.color': string;
    'createProject.preview': string;
    'createProject.processingImage': string;
    'createProject.createButton': string;
    'createProject.newProject': string;
    'createProject.success': string;
    'createProject.successDesc': string;
    'createProject.uploadFailedDesc': string;

    // Project Card Actions
    'projectCard.settings': string;
    'projectCard.archive': string;
    'projectCard.unarchive': string;
    'projectCard.delete': string;
    'projectCard.leaveProject': string;
    'projectCard.archived': string;
    'projectCard.tasks': string;

    // Settings page additional
    'settings.emailCannotBeChanged': string;
    'settings.chooseNotifications': string;
    'settings.linkDiscordDesc': string;
    'settings.uploadProfilePictureHint': string;

    // Toast messages for tasks
    'toast.taskCreated': string;
    'toast.taskCreatedDesc': string;
    'toast.taskUpdated': string;
    'toast.taskUpdatedDesc': string;
    'toast.taskDeleted': string;
    'toast.taskDeletedDesc': string;
    'toast.tasksDeleted': string;
    'toast.tasksDeletedDesc': string;
    'toast.statusUpdated': string;
    'toast.statusUpdatedDesc': string;
    'toast.priorityUpdated': string;
    'toast.priorityUpdatedDesc': string;
    'toast.assigneesUpdated': string;
    'toast.assigneesUpdatedDesc': string;
    'toast.viewSaved': string;
    'toast.viewSavedDesc': string;
    'toast.viewDeleted': string;
    'toast.errorSavingView': string;
    'toast.errorDeletingTasks': string;
    'toast.errorUpdatingTasks': string;
    'toast.subtaskCreated': string;
    'toast.subtaskCreatedDesc': string;
    'toast.dueDateUpdated': string;
    'toast.dueDateSet': string;
    'toast.dueDateCleared': string;
    'toast.fieldDeleted': string;
    'toast.fieldDeletedDesc': string;
    'toast.errorDeletingField': string;
    'toast.errorAssignees': string;
    'toast.errorDueDate': string;
    'toast.errorCustomField': string;
    'toast.errorTaskAction': string;

    // Custom field dialog
    'customFields.editFieldTitle': string;
    'customFields.addFieldTitle': string;
    'customFields.editFieldDescription': string;
    'customFields.addFieldDescription': string;
    'customFields.fieldNamePlaceholder': string;
    'customFields.saving': string;
    'customFields.createField': string;
    'customFields.noFieldsYet': string;
    'customFields.noFieldsHint': string;
    'customFields.optionsCount': string;
    'customFields.deleteFieldTitle': string;
    'customFields.deleteFieldDescription': string;
    'customFields.deleting': string;

    // Saved views panel
    'views.deleteViewTitle': string;
    'views.deleteViewConfirmation': string;

    // Landing page
    'landing.heroTitle': string;
    'landing.heroSubtitle': string;
    'landing.getStarted': string;
    'landing.tryDemo': string;
    'landing.feature.projects': string;
    'landing.feature.projectsDesc': string;
    'landing.feature.team': string;
    'landing.feature.teamDesc': string;
    'landing.feature.realtime': string;
    'landing.feature.realtimeDesc': string;
    'landing.feature.comments': string;
    'landing.feature.commentsDesc': string;
    'landing.feature.views': string;
    'landing.feature.viewsDesc': string;
    'landing.feature.themes': string;
    'landing.feature.themesDesc': string;
    'landing.feature.notifications': string;
    'landing.feature.notificationsDesc': string;
    'landing.feature.customFields': string;
    'landing.feature.customFieldsDesc': string;
    'landing.demo.title': string;
    'landing.demo.subtitle': string;
    'landing.demo.tryTheme': string;
    'landing.demo.taskDemo': string;
    'landing.demo.interactiveDemo': string;
    'landing.cta.title': string;
    'landing.cta.subtitle': string;
    'landing.scrollToExplore': string;
    'landing.builtWith': string;
    'landing.selfHosted': string;
    'landing.selfHostedDesc': string;
    'landing.openSource': string;
    'landing.freeForAll': string;
    'landing.freeForAllDesc': string;
    'landing.hostedForYou': string;
    'landing.hostedForYouDesc': string;
}

// ============================================
// TRANSLATIONS
// ============================================

export const translations: Record<Language, Translations> = {
    en: {
        // Common
        'common.save': 'Save',
        'common.saveChanges': 'Save Changes',
        'common.cancel': 'Cancel',
        'common.delete': 'Delete',
        'common.edit': 'Edit',
        'common.create': 'Create',
        'common.add': 'Add',
        'common.remove': 'Remove',
        'common.search': 'Search',
        'common.filter': 'Filter',
        'common.loading': 'Loading...',
        'common.noData': 'No data',
        'common.error': 'Error',
        'common.success': 'Success',
        'common.confirm': 'Confirm',
        'common.close': 'Close',
        'common.yes': 'Yes',
        'common.no': 'No',
        'common.none': 'None',
        'common.all': 'All',
        'common.clear': 'Clear',
        'common.actions': 'Actions',
        'common.settings': 'Settings',
        'common.name': 'Name',
        'common.email': 'Email',
        'common.password': 'Password',
        'common.submit': 'Submit',
        'common.back': 'Back',
        'common.next': 'Next',
        'common.previous': 'Previous',
        'common.today': 'Today',
        'common.tomorrow': 'Tomorrow',
        'common.yesterday': 'Yesterday',
        'common.overdue': 'Overdue',
        'common.unassigned': 'Unassigned',
        'common.viewAll': 'View all',

        // Navigation
        'nav.home': 'Dashboard',
        'nav.dashboard': 'Dashboard',
        'nav.projects': 'Projects',
        'nav.myTasks': 'My tasks',
        'nav.notifications': 'Notifications',
        'nav.settings': 'Settings',
        'nav.signOut': 'Sign out',
        'nav.favorites': 'Favorites',

        // Auth
        'auth.login': 'Login',
        'auth.register': 'Register',
        'auth.signIn': 'Sign in',
        'auth.signUp': 'Sign up',
        'auth.logout': 'Logout',
        'auth.forgotPassword': 'Forgot password?',
        'auth.resetPassword': 'Reset password',
        'auth.forgotPasswordTitle': 'Forgot your password?',
        'auth.forgotPasswordDescription': 'Enter your email and we will send you a secure reset link.',
        'auth.sendResetLink': 'Send reset link',
        'auth.sendingResetLink': 'Sending link...',
        'auth.resetEmailSent': 'If an account exists for this email, a reset link has been sent.',
        'auth.backToLogin': 'Back to sign in',
        'auth.resetPasswordDescription': 'Choose a new password for your account.',
        'auth.confirmNewPassword': 'Confirm new password',
        'auth.resettingPassword': 'Resetting password...',
        'auth.passwordResetSuccess': 'Your password has been reset. You can now sign in.',
        'auth.checkYourEmailTitle': 'Confirm your e-mail address',
        'auth.checkYourEmailBody': 'We sent you a verification link.',
        'auth.checkYourEmailHint': "Click the link in the e-mail to activate your account. Don't forget to check your spam folder.",
        'auth.verifyingEmail': 'Verifying your e-mail address…',
        'auth.emailVerifiedTitle': 'E-mail confirmed',
        'auth.emailVerifiedBody': 'Your account is now active. Welcome aboard!',
        'auth.goToDashboard': 'Go to my dashboard',
        'auth.verifyEmailFailedTitle': 'Verification failed',
        'auth.verifyEmailFailedBody': 'This verification link is invalid or has expired.',
        'auth.resendVerification': 'Resend the verification link',
        'auth.resendVerificationHint': 'Enter your e-mail address to receive a new link.',
        'auth.verificationResentTitle': 'Link sent',
        'auth.verificationResentBody': 'If an unverified account exists for this address, a new link is on its way.',
        'auth.loginUnverifiedBody': 'This account has not been confirmed yet. Open the link we e-mailed you, or request a new one.',
        'auth.backToSignIn': 'Back to sign in',
        'auth.sending': 'Sending…',
        'auth.invalidResetLink': 'This reset link is invalid or has expired.',
        'auth.rememberMe': 'Remember me for 30 days',
        'auth.noAccount': "Don't have an account?",
        'auth.hasAccount': 'Already have an account?',
        'auth.haveAccount': 'Already have an account?',
        'auth.emailPlaceholder': 'you@example.com',
        'auth.passwordPlaceholder': 'Enter your password',
        'auth.namePlaceholder': 'Azalina White',
        'auth.confirmPassword': 'Confirm password',
        'auth.currentPassword': 'Current password',
        'auth.newPassword': 'New password',
        'auth.invalidCredentials': 'Invalid email or password',
        'auth.emailRequired': 'Email is required',
        'auth.passwordRequired': 'Password is required',
        'auth.nameRequired': 'Name is required',
        'auth.passwordMinLength': 'Password must be at least 8 characters',
        'auth.passwordsDoNotMatch': 'Passwords do not match',
        'auth.welcomeBack': 'Welcome back',
        'auth.createAccount': 'Create an account',
        'auth.createAccountBtn': 'Create account',
        'auth.creatingAccount': 'Creating account...',
        'auth.getStarted': 'Get started with Project Manager',
        'auth.signInToAccount': 'Sign in to your Project Manager account',
        'auth.signingIn': 'Signing in...',
        'auth.email': 'Email',
        'auth.password': 'Password',
        'auth.name': 'Name',
        'auth.password8Chars': 'At least 8 characters',
        'auth.passwordUppercase': 'One uppercase letter',
        'auth.passwordLowercase': 'One lowercase letter',
        'auth.passwordNumber': 'One number',
        // OAuth
        'auth.continueWithGoogle': 'Continue with Google',
        'auth.continueWithDiscord': 'Continue with Discord',
        'auth.orContinueWith': 'or continue with email',
        'auth.oauthDenied': 'Access was denied. Please try again.',
        'auth.oauthInvalidRequest': 'Invalid request. Please try again.',
        'auth.oauthInvalidState': 'Invalid state. Please try again.',
        'auth.oauthTokenFailed': 'Failed to authenticate. Please try again.',
        'auth.oauthUserFetchFailed': 'Could not retrieve your account information.',
        'auth.oauthEmailRequired': 'Email address is required for registration.',
        'auth.oauthServerError': 'An error occurred. Please try again later.',

        // Projects
        'projects.title': 'Projects',
        'projects.createProject': 'Create Project',
        'projects.newProject': 'New Project',
        'projects.editProject': 'Edit Project',
        'projects.deleteProject': 'Delete Project',
        'projects.archiveProject': 'Archive Project',
        'projects.unarchiveProject': 'Unarchive Project',
        'projects.leaveProject': 'Leave Project',
        'projects.projectName': 'Project name',
        'projects.projectDescription': 'Description',
        'projects.projectColor': 'Color',
        'projects.noProjects': 'No projects yet',
        'projects.searchProjects': 'Search projects...',
        'projects.showArchived': 'Show Archived',
        'projects.showActive': 'Show Active',
        'projects.hideArchived': 'Hide archived',
        'projects.members': 'Members',
        'projects.tasks': 'Tasks',
        'projects.invite': 'Invite',
        'projects.inviteMembers': 'Invite members',
        'projects.manageProjects': 'Manage your projects and collaborate with your team',
        'projects.noProjectsFound': 'No projects found',
        'projects.noArchivedProjects': 'No archived projects',
        'projects.tryDifferentSearch': 'Try a different search term',
        'projects.archiveToSeeHere': 'Archive projects to see them here',
        'projects.createFirstProject': 'Create your first project to get started',
        'projects.deleteConfirmation': 'Are you sure you want to delete this project? This action cannot be undone. All tasks, comments, and attachments will be permanently deleted.',
        'projects.leaveConfirmation': 'Are you sure you want to leave',

        // Project aliases
        'project.createProject': 'Create project',
        'project.newProject': 'New project',
        'project.manageProjects': 'Manage your projects and collaborate with your team',
        'project.searchProjects': 'Search projects...',
        'project.showActive': 'Show active',
        'project.showArchived': 'Show archived',
        'project.noProjectsFound': 'No projects found',
        'project.noArchivedProjects': 'No archived projects',
        'project.noProjects': 'No projects yet',
        'project.tryDifferentSearch': 'Try a different search term',
        'project.archiveToSeeHere': 'Archive projects to see them here',
        'project.createFirstProject': 'Create your first project to get started',
        'project.deleteProject': 'Delete project',
        'project.leaveProject': 'Leave project',
        'project.deleteConfirmation': 'Are you sure you want to delete this project? This action cannot be undone. All tasks, comments, and attachments will be permanently deleted.',
        'project.leaveConfirmation': 'Are you sure you want to leave this project?',
        'projects.inviteSent': 'Invitation sent',
        'projects.projectSettings': 'Project Settings',
        'projects.dangerZone': 'Danger Zone',
        'projects.deleteWarning': 'This action cannot be undone. This will permanently delete the project and all its data.',
        'projects.owner': 'Owner',
        'projects.admin': 'Admin',
        'projects.member': 'Member',
        'projects.viewer': 'Viewer',
        'projects.currentProject': 'Current project',
        'projects.manageTeam': 'Manage team',
        'projects.general': 'General',
        'projects.teamMembers': 'Team members',
        'projects.customization': 'Customization',
        'projects.projectIcon': 'Project icon',
        'projects.useEmoji': 'Use emoji',
        'projects.useImage': 'Use image',
        'projects.selectEmoji': 'Select an emoji',
        'projects.uploadImage': 'Upload image',
        'projects.changeImage': 'Change image',
        'projects.removeImage': 'Remove',
        'projects.imageHint': 'PNG, JPG, GIF up to 5MB',
        'projects.inviteEmail': 'Email address',
        'projects.role': 'Role',
        'projects.expires': 'Expires',
        'projects.expiresInDays': 'Expires in',
        'projects.day': 'day',
        'projects.days': 'days',
        'projects.pendingInvitations': 'Pending invitations',
        'projects.cancelInvitation': 'Cancel invitation',
        'projects.copyLink': 'Copy invite link',
        'projects.linkCopied': 'Link copied to clipboard',
        'projects.removeMember': 'Remove from project',
        'projects.changeRole': 'Change role',
        'projects.noMembers': 'No team members yet',
        'projects.inviteDescription': 'Invite team members to collaborate on this project',
        'projects.invalidFileType': 'Please select an image file',
        'projects.fileTooLarge': 'Please select an image smaller than 5MB',

        // Tasks
        'tasks.title': 'Tasks',
        'tasks.myTasks': 'My tasks',
        'tasks.createTask': 'Create task',
        'tasks.newTask': 'New task',
        'tasks.editTask': 'Edit task',
        'tasks.deleteTask': 'Delete task',
        'tasks.taskName': 'Task name',
        'tasks.taskDescription': 'Description',
        'tasks.noTasks': 'No tasks yet',
        'tasks.noTasksDescription': 'Create a task to get started',
        'tasks.addTask': 'Add task',
        'tasks.addSubtask': 'Add subtask',
        'tasks.taskDetails': 'Task details',
        'tasks.resizePanel': 'Resize task details panel',
        'tasks.subtasks': 'Subtasks',
        'tasks.dependsOn': 'Depends on',
        'tasks.dependsOnEmpty': 'No dependencies yet.',
        'tasks.dependsOnNoMatches': 'No tasks match that search.',
        'tasks.dependsOnSearchPlaceholder': 'Search tasks to add as a dependency...',
        'tasks.assignees': 'Assignees',
        'tasks.assignTo': 'Assign to',
        'tasks.selectAssignees': 'Select assignees',
        'tasks.dueDate': 'Due date',
        'tasks.createdAt': 'Created at',
        'tasks.customFieldsSection': 'Custom fields',
        'tasks.customFieldAdd': '+ Add',
        'tasks.customFieldNone': 'None',
        'tasks.setDueDate': 'Set due date',
        'tasks.clearDate': 'Clear date',
        'tasks.startDate': 'Start date',
        'tasks.endDate': 'End date',
        'tasks.notSet': 'Not set',
        'tasks.status': 'Status',
        'tasks.priority': 'Priority',
        'tasks.comments': 'Comments',
        'tasks.attachments': 'Attachments',
        'tasks.task': 'Task',
        'tasks.selected': 'selected',
        'tasks.setStatus': 'Set status',
        'tasks.setPriority': 'Set priority',
        'tasks.noMatchingTasks': 'No matching tasks',
        'tasks.tryAdjustingFilters': 'Try adjusting your filters to find tasks.',
        'tasks.getStartedCreatingTask': 'Get started by creating your first task for this project.',
        'tasks.createFirstTask': 'Create your first task',
        'tasks.assign': 'Assign',
        'tasks.customFields': 'Custom fields',
        'tasks.columns': 'Columns',
        'tasks.noTasksFound': 'No tasks found.',
        'tasks.enterTitle': 'Enter task title...',
        'tasks.enterSubtaskTitle': 'Enter subtask title...',
        'tasks.clearValue': 'Clear value',
        'tasks.editIndividually': 'Edit individually',
        'tasks.toggleOptions': 'Toggle options for selected tasks',
        'tasks.checked': 'Checked',
        'tasks.unchecked': 'Unchecked',
        'tasks.deleteTaskAction': 'Delete task',
        'tasks.loadingSubtasks': 'Loading subtasks...',
        'tasks.editTitle': 'Edit title',

        // Task aliases
        'task.today': 'Today',
        'task.tomorrow': 'Tomorrow',
        'task.overdue': 'Overdue',
        'task.noTasks': 'No tasks',
        'task.noTasksDescription': 'No tasks found matching your filters',

        // Task Status
        'status.todo': 'Todo',
        'status.inProgress': 'In progress',
        'status.inReview': 'In review',
        'status.done': 'Done',
        'status.cancelled': 'Cancelled',

        // Task Priority
        'priority.none': 'None',
        'priority.low': 'Low',
        'priority.medium': 'Medium',
        'priority.high': 'High',
        'priority.urgent': 'Urgent',

        // Views
        'views.savedViews': 'Saved views',
        'views.saveView': 'Save view',
        'views.editView': 'Edit',
        'views.editViewTitle': 'Edit view',
        'views.deleteView': 'Delete',
        'views.duplicateView': 'Duplicate',
        'views.setDefault': 'Set as default',
        'views.setAsDefault': 'Set as default view',
        'views.shareWithTeam': 'Share with team members',
        'views.viewName': 'View name',
        'views.viewNamePlaceholder': 'e.g., My tasks, high priority',
        'views.saveViewDescription': 'Save the current filter and column configuration as a view.',
        'views.editViewDescription': 'Update the saved view settings.',
        'views.allTasks': 'All tasks',
        'views.noViews': 'No saved views yet. Configure filters and save your view.',
        'views.isDefault': 'Set as default view',
        'views.isShared': 'Share with team members',
        'views.saveCurrentView': 'Save the current filter and column configuration as a view.',
        'views.favorites': 'Favorites',
        'views.personal': 'Personal',
        'views.shared': 'Shared',
        'views.noFavorites': 'No favorite views yet',
        'views.noPersonalViews': 'No personal views yet',
        'views.noSharedViews': 'No shared views yet',
        'views.addToFavorites': 'Add to favorites',
        'views.removeFromFavorites': 'Remove from favorites',
        'views.isFavorite': 'Is favorite',

        // View type toggle
        'viewType.table': 'Table',
        'viewType.kanban': 'Kanban',
        'viewType.calendar': 'Calendar',
        'viewType.gantt': 'Gantt',
        'viewType.document': 'Document',
        'viewType.toggleAriaLabel': 'Switch between table, kanban, calendar, gantt, and document views',

        // R14: Document mode (English)
        'document.defaultTabName': 'General',
        'document.newTab': 'New tab',
        'document.closeTab': 'Close tab',
        'document.tabAriaLabel': 'Document tab',
        'document.tabsAriaLabel': 'Document tabs',
        'document.emptyTitle': 'No document yet',
        'document.emptyBody': 'Create a new document to start writing. Documents sync live between collaborators and auto-save.',
        'document.emptyCreateCta': 'Create document',
        'document.readOnlyBadge': 'Read-only',
        'document.loading': 'Loading document…',
        'document.connecting': 'Connecting to collaboration server…',
        'document.connected': 'Live',
        'document.disconnected': 'Offline',
        'document.saveStatus.saving': 'Saving…',
        'document.saveStatus.saved': 'Saved',
        'document.saveStatus.error': 'Save failed',
        'document.saveStatus.dirty': 'Unsaved changes',
        'document.saveStatus.errorHint':
            'Your changes could not be uploaded. They stay unsaved — we will retry automatically.',
        'document.lastSavedAt': 'Last saved {time}',
        'document.collab.peers': '{count} other editors',
        'document.collab.onePeer': '1 other editor',
        'document.collab.zeroPeers': 'Just you',
        'document.toolbar.bold': 'Bold',
        'document.toolbar.italic': 'Italic',
        'document.toolbar.underline': 'Underline',
        'document.toolbar.h1': 'Heading 1',
        'document.toolbar.h2': 'Heading 2',
        'document.toolbar.h3': 'Heading 3',
        'document.toolbar.bulletList': 'Bullet list',
        'document.toolbar.orderedList': 'Numbered list',
        'document.toolbar.blockquote': 'Blockquote',
        'document.toolbar.paragraph': 'Paragraph',
        'document.toolbar.fontFamily': 'Font',
        'document.toolbar.fontSize': 'Size',
        'document.toolbar.fontFamily.sans': 'Sans-serif',
        'document.toolbar.fontFamily.arial': 'Arial',
        'document.toolbar.fontFamily.serif': 'Georgia (Serif)',
        'document.toolbar.fontFamily.times': 'Times New Roman',
        'document.toolbar.fontFamily.verdana': 'Verdana',
        'document.toolbar.fontFamily.courier': 'Courier New',
        'document.toolbar.fontFamily.mono': 'System Monospace',
        'document.toolbar.fontFamily.comic': 'Comic Sans',
        'document.toolbar.fontFamily.default': 'Default font',
        'document.toolbar.fontSize.small': 'Small',
        'document.toolbar.fontSize.medium': 'Medium',
        'document.toolbar.fontSize.large': 'Large',
        'document.toolbar.fontSize.xs': 'Extra small',
        'document.toolbar.fontSize.xl': 'Extra large',
        'document.toolbar.fontSize.xxl': 'Huge',
        'document.toolbar.fontSize.default': 'Default size',
        'document.toolbar.fontSizeCustomHint': 'Set an exact pixel size (6–200)',
        'document.toolbar.color': 'Text colour',
        'document.toolbar.colorCustom': 'Custom',
        'document.toolbar.colorClear': 'Clear colour',
        'document.toolbar.tocTitle': 'Table of contents',
        'document.toolbar.tocEmpty': 'No headings yet — add a heading to populate the TOC.',
        'document.toolbar.tocUntitled': '(untitled)',
        'document.toolbar.insertTable': 'Insert table',
        'document.toolbar.addRowBefore': 'Insert row above',
        'document.toolbar.addColumnBefore': 'Insert column left',
        'document.toolbar.mergeCells': 'Merge cells',
        'document.toolbar.splitCell': 'Split cell',
        'document.toolbar.toggleHeaderRow': 'Toggle header row',
        'document.toolbar.toggleHeaderColumn': 'Toggle header column',
        'document.toolbar.addRowAfter': 'Insert row below',
        'document.toolbar.addColumnAfter': 'Insert column right',
        'document.toolbar.deleteRow': 'Delete row',
        'document.toolbar.deleteColumn': 'Delete column',
        'document.toolbar.deleteTable': 'Delete table',
        'document.toolbar.undo': 'Undo',
        'document.toolbar.redo': 'Redo',
        // R20: text alignment (left / center / right / justify)
        'document.toolbar.alignLeft': 'Align left',
        'document.toolbar.alignCenter': 'Center',
        'document.toolbar.alignRight': 'Align right',
        'document.toolbar.alignJustify': 'Justify',
        'document.toolbar.alignLeftHint': 'Left-align the current paragraph',
        'document.toolbar.alignCenterHint': 'Center the current paragraph',
        'document.toolbar.alignRightHint': 'Right-align the current paragraph',
        'document.toolbar.alignJustifyHint': 'Justify the current paragraph',
        // R18 (t59): export-to-PDF / DOCX button in the toolbar.
        'document.exportMenu': 'Export',
        'document.exportPdf': 'Export as PDF',
        'document.exportDocx': 'Export as DOCX',
        'document.exportTitle': 'Download the document',
        'document.editorPlaceholder': 'Start typing — or press / for commands',
        'document.deleteViewConfirm': 'Delete this document?',
        'document.tableHint': 'Tip: type +table=3x4 + space to insert a 3×4 table.',
        // R14: @-mention picker (English)
        'document.mention.loading': 'Loading tasks…',
        'document.mention.empty': 'No matching tasks',
        'document.mention.pickerAriaLabel': 'Mention a task or person',
        'document.mention.task': 'Task',
        'document.mention.person': 'Person',
        'document.mention.searchPlaceholder': 'Search tasks or people…',
        'document.mention.searchAriaLabel': 'Search mentions',
        'document.mention.section.tasks': 'Tasks',
        'document.mention.section.people': 'People',
        'document.mention.notified': 'You mentioned {name}',
        'document.mention.notifiedTitle': 'Mention sent',
        'document.mention.emptyAfterSearch': 'No matches for "{query}"',
        // R25: Bug A server-search status + Bug B person-mention popover
        'document.mention.searchUnavailable': 'Search unavailable — showing loaded tasks',
        'document.mention.loadMore': 'Load more',
        'document.mention.personTasksTitle': 'Assigned tasks',
        'document.mention.personTasksEmpty': 'No tasks assigned to this person',
        'document.mention.personTasksError': 'Could not load tasks',
        'calendar.comingSoonTitle': 'Calendar view coming soon',
        'calendar.comingSoonBody': 'The Calendar view will show tasks on a month / week / agenda grid. Meanwhile you can keep using Table or Kanban.',
        'calendar.activeViewLabel': 'Active view',
        'calendar.previous': 'Previous',
        'calendar.next': 'Next',
        'calendar.settings': 'Settings',
        'calendar.viewModeLabel': 'View mode',
        'calendar.dateSourceLabel': 'Date source',
        'calendar.colorByLabel': 'Color events by',
        'calendar.viewMode.month': 'Month',
        'calendar.viewMode.week': 'Week',
        'calendar.viewMode.agenda': 'Agenda',
        'calendar.dateSource.dueDate': 'Due date',
        'calendar.dateSource.startDate': 'Start date',
        'calendar.dateSource.endDate': 'End date',
        'calendar.dateSource.sprintStart': 'Sprint start',
        'calendar.dateSource.sprintEnd': 'Sprint end',
        'calendar.colorBy.status': 'Status',
        'calendar.colorBy.priority': 'Priority',
        'calendar.colorBy.assignee': 'Assignee',
        'calendar.requiresSprint': 'requires a Sprint field',
        'calendar.weekday.0': 'Sun',
        'calendar.weekday.1': 'Mon',
        'calendar.weekday.2': 'Tue',
        'calendar.weekday.3': 'Wed',
        'calendar.weekday.4': 'Thu',
        'calendar.weekday.5': 'Fri',
        'calendar.weekday.6': 'Sat',
        'calendar.more': 'more',
        'calendar.agendaEmptyTitle': 'No upcoming tasks',
        'calendar.agendaEmptyBody': 'Tasks with a date on the active date source will appear here.',
        'calendar.settingsTitle': 'Calendar settings',
        'calendar.settingsHint': 'Choose how the Calendar view renders tasks on the grid.',
        'calendar.errorUpdateDate': 'Failed to move the task to the new date.',
        'calendar.loadError': 'Failed to load the calendar',
        // t18: right-click context menu
        'calendar.contextMenu.createTaskAt': 'Create task at {date}',
        'calendar.contextMenu.openTask': 'Open task',
        'calendar.contextMenu.deleteTask': 'Delete task',
        // T16 R3: settings-panel filter-rule editor
        'calendar.filterStatusLabel': 'Filter by status',
        'calendar.filterStatusHint': 'Untick to hide tasks with that status.',
        'calendar.filterClear': 'Show all',
        'calendar.subtasksLabel': 'Subtasks',
        'calendar.subtasksEmpty': 'No subtasks',
        // T21 R4: "All tasks" date source + Periods section
        'calendar.dateSource.all': 'All tasks',
        'calendar.periodsLabel': 'Periods',
        'calendar.periodsHint': 'Date ranges (e.g. absences) rendered as bands behind the calendar grid.',
        'calendar.periodAdd': 'Add period',
        'calendar.periodRemove': 'Remove period',
        'calendar.periodClearAll': 'Clear all',
        'calendar.periodNamePlaceholder': 'Period name (e.g. Absence)',
        'calendar.periodStart': 'Start date',
        'calendar.periodEnd': 'End date',
        'calendar.periodColor': 'Period color',
        'gantt.comingSoonTitle': 'Gantt view coming soon',
        'gantt.comingSoonBody': 'The Gantt view will show tasks as horizontal bars across a timeline. Meanwhile you can keep using Table or Kanban.',
        'gantt.activeViewLabel': 'Active view',
        'gantt.previous': 'Previous',
        'gantt.next': 'Next',
        'gantt.settings': 'Settings',
        'gantt.timeScaleLabel': 'Time scale',
        'gantt.timeScale.day': 'Day',
        'gantt.timeScale.week': 'Week',
        'gantt.timeScale.month': 'Month',
        'gantt.timeScale.auto': 'Auto',
        'gantt.groupByLabel': 'Group rows by',
        'gantt.groupBy.status': 'Status',
        'gantt.groupBy.assignee': 'Assignee',
        'gantt.groupBy.none': 'None',
        'gantt.showSprintsLabel': 'Show sprint bands',
        'gantt.showSprintsHint': 'Overlay sprint boundaries across the timeline.',
        // R3 / t17
        'gantt.showDatelessLabel': 'Show tasks without dates',
        'gantt.showDatelessHint': 'Dateless tasks are hidden by default — flip this on to list them at the bottom of each group.',
        'gantt.expandSubtasks': 'Expand subtasks',
        'gantt.collapseSubtasks': 'Collapse subtasks',
        // R4 / t22: right-click → cursor-arrow pick-target flow
        'gantt.depMenuSingle': 'Add a dependency on this task…',
        'gantt.depPickHint': 'Click a target task to create the dependency. Right-click to cancel.',
        'gantt.depPickCancelHint': 'Picking cancelled',
        'gantt.addDependency': 'Add a dependency',
        'gantt.removeDependency': 'Remove a dependency',
        'gantt.addDependencyTitle': 'Add a dependency',
        'gantt.addDependencyDescription': 'Pick a direction, target task, and dependency type.',
        'gantt.removeDependencyTitle': 'Remove dependencies',
        'gantt.removeDependencyDescription': 'Delete the edges attached to this task.',
        'gantt.dependencyDirection': 'Direction',
        'gantt.dependencyAsSuccessor': 'This task waits for another',
        'gantt.dependencyAsPredecessor': 'Another task waits for this one',
        'gantt.dependencyTarget': 'Target task',
        'gantt.dependencySearchPlaceholder': 'Search tasks...',
        'gantt.dependencyType': 'Dependency type',
        'gantt.dependencyTypeFS': 'Finish to Start — task B starts after task A finishes.',
        'gantt.dependencyTypeSS': 'Start to Start — task B starts when task A starts.',
        'gantt.dependencyTypeFF': 'Finish to Finish — task B finishes when task A finishes.',
        'gantt.dependencyTypeSF': 'Start to Finish — task B finishes when task A starts.',
        'gantt.settingsTitle': 'Gantt settings',
        'gantt.settingsHint': 'Choose how the Gantt view lays out tasks on the timeline.',
        'gantt.emptyTitle': 'No tasks on the timeline',
        'gantt.emptyBody': 'Tasks with a start date and an end date will appear here. Edit a task to add dates.',
        'gantt.errorUpdateDates': 'Failed to update the task dates.',
        'gantt.loadError': 'Failed to load the Gantt view',
        'gantt.requiresSprint': 'requires a Sprint field',
        'gantt.bar.handleAria': 'Drag to move the task',
        'gantt.bar.moveAria': 'Drag to move',
        'gantt.bar.resizeEndAria': 'Drag to resize the end date',
        'gantt.bar.resizeStartAria': 'Drag to resize the start date',
        'gantt.bar.assignedTo': 'Assigned to',
        'gantt.status.TODO': 'To do',
        'gantt.status.IN_PROGRESS': 'In progress',
        'gantt.status.IN_REVIEW': 'In review',
        'gantt.status.DONE': 'Done',
        'gantt.status.CANCELLED': 'Cancelled',
        'gantt.unassigned': 'Unassigned',
        'gantt.filtersLabel': 'Filters',
        'kanban.subtaskButton': 'Show subtasks ({{count}})',
        'kanban.subtaskPopoverTitle': 'Subtasks',
        'kanban.subtaskEmpty': 'No subtasks yet.',

        // Kanban settings cog popover (t15)
        'kanban.settings': 'Board settings',
        'kanban.settingsTitle': 'Board settings',
        'kanban.settingsHint': 'Tweak what shows on the Kanban board.',
        'kanban.cardFieldsSection': 'Card fields',
        'kanban.columnVisibilitySection': 'Columns',

        // Kanban
        'kanban.cardFieldsTitle': 'Card content',
        'kanban.cardFieldsHint': 'Pick which custom fields appear as chips on each card.',
        'kanban.noCardFieldsAvailable': 'No renderable custom fields yet.',
        'kanban.columnGrouping': 'Columns grouped by',
        'kanban.columnGroupingHint': 'The field whose values become the board columns.',
        'kanban.dropHint': 'Drop a card here',
        'kanban.addColumn': 'Add column',
        'kanban.addColumnTitle': 'Add column',
        'kanban.addColumnDescription': 'Define a column with a name, optional colour and one or more filter rules. Tasks matching every rule land in this column.',
        'kanban.columnName': 'Column name',
        'kanban.columnNamePlaceholder': 'e.g. In review, Blocked, Backend',
        'kanban.columnColor': 'Accent colour',
        'kanban.columnFilterRules': 'Filter rules',
        'kanban.addRule': 'Add rule',
        'kanban.columns': 'Columns',
        'kanban.columnsVisibility': 'Toggle column visibility',
        'kanban.columnsVisibilityHint': 'Hidden columns stay in the view but their tasks fall through to Other.',
        'kanban.noColumns': 'No columns yet — add one to get started.',
        'kanban.noColumnsTitle': 'No columns yet',
        'kanban.noColumnsDescription': 'This Kanban view is empty. Add your first column to start organising tasks.',
        'kanban.createFirstColumn': 'Create your first column',
        'kanban.cardFields': 'Card fields',
        'kanban.columnCreated': 'Column created',
        'kanban.columnUpdated': 'Column updated',
        'kanban.columnDeleted': 'Column deleted',
        'kanban.errorCreateColumn': 'Could not create column',
        'kanban.errorDeleteColumn': 'Could not delete column',
        'kanban.errorUpdateColumn': 'Could not update column',
        'kanban.errorReorderColumns': 'Could not reorder columns',
        'kanban.cannotDropHere': 'Cannot drop here',
        'kanban.cannotDropHereDescription': 'This column has multiple filter rules. Drag a card into one of the simpler status columns.',
        'kanban.editColumnTitle': 'Edit column',
        'kanban.editColumnDescription': 'Rename, recolour or refine the filter rules for this column.',
        'kanban.contextMenuEdit': 'Edit column…',
        'kanban.contextMenuHide': 'Hide column',
        'kanban.contextMenuDelete': 'Delete column',
        'kanban.fieldTag.status': 'Status',
        'kanban.fieldTag.priority': 'Priority',
        'kanban.fieldTag.assignee': 'Assignee',
        'kanban.fieldTag.customField': 'Custom field',
        'kanban.fieldTag.other': 'Other',
        'kanban.operator.eq': 'equals',
        'kanban.operator.neq': 'not equals',
        'kanban.operator.in': 'is one of',
        'kanban.operator.notIn': 'is not one of',
        'kanban.operator.isEmpty': 'is empty',
        'kanban.operator.isNotEmpty': 'is not empty',
        'kanban.operator.contains': 'contains',
        'kanban.field.status': 'Status',
        'kanban.field.priority': 'Priority',
        'kanban.field.assignee': 'Assignee',
        'kanban.field.customField': 'Custom field',
        'kanban.statusValue.TODO': 'To do',
        'kanban.statusValue.IN_PROGRESS': 'In progress',
        'kanban.statusValue.IN_REVIEW': 'In review',
        'kanban.statusValue.DONE': 'Done',
        'kanban.statusValue.CANCELLED': 'Cancelled',
        'kanban.priorityValue.NONE': 'None',
        'kanban.priorityValue.LOW': 'Low',
        'kanban.priorityValue.MEDIUM': 'Medium',
        'kanban.priorityValue.HIGH': 'High',
        'kanban.priorityValue.URGENT': 'Urgent',

        // Filters
        'filters.filters': 'Filters',
        'filters.clearFilters': 'Clear filters',
        'filters.clearAll': 'Clear all',
        'filters.filterByStatus': 'Filter by status',
        'filters.filterByPriority': 'Filter by priority',
        'filters.filterByAssignee': 'Filter by assignee',
        'filters.filterByDueDate': 'Filter by due date',
        'filters.activeFilters': 'Active filters',
        'filters.status': 'Status',
        'filters.priority': 'Priority',
        'filters.assignee': 'Assignee',
        'filters.allAssignees': 'All assignees',
        'filters.filter': 'filter',
        'filters.saveAsView': 'Save as view',
        'filters.saveViewChanges': 'Save view changes',
        'filters.addField': 'Add field',
        'filters.searchTasks': 'Search tasks...',
        'filter.operator.contains': 'contains',
        'filter.operator.notContains': 'does not contain',
        'filter.operator.equals': 'equals',
        'filter.operator.notEquals': 'does not equal',
        'filter.operator.startsWith': 'starts with',
        'filter.operator.endsWith': 'ends with',
        'filter.operator.lt': '<',
        'filter.operator.lte': '≤',
        'filter.operator.gt': '>',
        'filter.operator.gte': '≥',
        'filter.operator.between': 'between',
        'filter.operator.before': 'before',
        'filter.operator.after': 'after',
        'filter.operator.is': 'is',
        'filter.operator.isNot': 'is not',
        'filter.operator.isAnyOf': 'is any of',
        'filter.operator.isNoneOf': 'is none of',
        'filter.operator.containsAll': 'contains all',
        'filter.operator.containsAny': 'contains any',
        'filter.operator.isEmpty': 'is empty',
        'filter.operator.isNotEmpty': 'is not empty',
        'filter.operator.isTrue': 'is true',
        'filter.operator.isFalse': 'is false',
        'filter.valuePlaceholder.text': 'Type a value...',
        'filter.valuePlaceholder.number': 'Number',
        'filter.valuePlaceholder.select': 'Select...',
        'filter.valuePlaceholder.selectMulti': 'Select values...',
        'filter.valuePlaceholder.selectPerson': 'Select person...',
        'filter.selected': 'selected',
        'filter.addFilter': 'Filter',
        'filter.filterByField': 'Filter by field',
        'filter.group.text': 'Text',
        'filter.group.number': 'Numbers',
        'filter.group.date': 'Dates',
        'filter.group.category': 'Categories',
        'filter.group.person': 'People',

        // Comments
        'comments.addComment': 'Add comment',
        'comments.editComment': 'Edit comment',
        'comments.deleteComment': 'Delete comment',
        'comments.deleteTitle': 'Delete comment',
        'comments.deleteConfirm': 'Are you sure you want to delete this comment? This action cannot be undone.',
        'comments.noComments': 'No comments yet',
        'comments.beFirstToComment': 'Be the first to comment!',
        'comments.commentPlaceholder': 'Write a comment...',
        'comments.editPlaceholder': 'Edit your comment...',
        'comments.edited': 'edited',
        'comments.submitHint': 'Press ⌘+Enter to submit',
        'comments.mentionCount': 'mentioned',
        'comments.mentionHint': 'Type @ to mention someone',
        'comments.isTyping': 'is typing...',
        'comments.areTyping': 'are typing...',
        'comments.andOthersTyping': 'and {count} others are typing...',
        'comments.save': 'Save',
        'comments.comment': 'Comment',
        'comments.failedToLoad': 'Failed to load comments',

        // Attachments
        'attachments.addAttachment': 'Add attachment',
        'attachments.uploadFile': 'Upload file',
        'attachments.upload': 'Upload',
        'attachments.noAttachments': 'No attachments yet',
        'attachments.noAttachmentsHint': 'Click Upload to add files.',
        'attachments.download': 'Download',
        'attachments.deleteAttachment': 'Delete attachment',
        'attachments.deleteTitle': 'Delete attachment',
        'attachments.deleteConfirm': 'Are you sure you want to delete this attachment? This action cannot be undone.',
        'attachments.uploaded': 'uploaded',
        'attachments.failedToLoad': 'Failed to load attachments',
        'attachments.fileTooLarge': 'File size must be less than 50MB',
        'attachments.openInNewTab': 'Open in new tab',

        // Notifications
        'notifications.title': 'Notifications',
        'notifications.markAllRead': 'Mark all as read',
        'notifications.noNotifications': 'No notifications',
        'notifications.viewInvitation': 'View invitation',
        'notifications.taskAssigned': 'Task assigned',
        'notifications.taskAssignedDesc': 'When a task is assigned to you',
        'notifications.taskUpdated': 'Task updated',
        'notifications.taskUpdatedDesc': 'When an assigned task is modified',
        'notifications.commentAdded': 'New comment',
        'notifications.commentAddedDesc': 'When someone comments on your task',
        'notifications.attachmentAdded': 'New attachment',
        'notifications.attachmentAddedDesc': 'When an attachment is added to your task',
        'notifications.mentioned': 'Mentions',
        'notifications.mentionedDesc': 'When someone mentions you',
        'notifications.projectInvite': 'Project invitations',
        'notifications.projectInviteDesc': 'When you are invited to a project',
        'notifications.dueDateReminder': 'Due date reminders',
        'notifications.dueDateReminderDesc': 'Reminders before tasks are due',
        'notifications.taskOverdue': 'Overdue tasks',
        'notifications.taskOverdueDesc': 'When a task becomes overdue',

        // Settings
        'settings.title': 'Settings',
        'settings.profile': 'Profile',
        'settings.profileInfo': 'Profile information',
        'settings.profilePicture': 'Profile picture',
        'settings.uploadPicture': 'Upload picture',
        'settings.removePicture': 'Remove',
        'settings.changePassword': 'Change password',
        'settings.updatePassword': 'Update password',
        'settings.language': 'Language',
        'settings.language.title': 'Language',
        'settings.language.description': 'Choose your preferred language for the interface',
        'settings.language.select': 'Select Language',
        'settings.selectLanguage': 'Select language',
        'settings.notificationSettings': 'Notification settings',
        'settings.discordIntegration': 'Discord integration',
        'settings.discordIntegrationDesc': 'Connect your Discord account to receive notifications',
        'settings.connectDiscord': 'Connect Discord',
        'settings.disconnectDiscord': 'Disconnect',
        'settings.discordConnected': 'Connected',
        'settings.discordReady': 'Discord notifications ready',
        'settings.discordReadyDescription': 'The private test message was delivered successfully.',
        'settings.discordNeedsReconnect': 'Discord needs to be reconnected',
        'settings.reconnectDiscord': 'Reconnect',
        'settings.discordLinkStepInstall': 'Install Project Manager to your Discord user account.',
        'settings.discordLinkStepCommand': 'In any server or conversation, run /link with this code. The reply is private.',
        'settings.discordCommandHint': 'In any Discord text field, enter: /link code:{code}',
        'settings.installDiscordApp': 'Install / open Discord',
        'settings.copyCode': 'Copy code',
        'settings.codeCopied': 'Discord link code copied',
        'settings.inAppNotifications': 'In-App notifications',
        'settings.discordNotifications': 'Discord notifications',
        'settings.emailFallbackNotifications': 'E-mail fallback',
        'settings.emailFallbackDescription': 'Only if Discord was not delivered',
        'settings.reminderTiming': 'Due date reminder timing',
        'settings.reminderTimingDesc': 'When should we remind you about upcoming due dates?',
        'settings.before1h': '1 hour before',
        'settings.before24h': '24 hours before',
        'settings.before48h': '48 hours before',
        'settings.after24h': '24 hours after overdue',
        'settings.after48h': '48 hours after overdue',
        // Theme settings
        'settings.theme': 'Theme',
        'settings.themeTitle': 'Appearance',
        'settings.themeDescription': 'Customize the look and feel of the application',
        'settings.interactiveEffects': 'Interactive effects',
        'settings.interactiveEffectsDesc': 'Enable animated particles and cursor interactions',

        // Dashboard
        'dashboard.welcome': 'Welcome',
        'dashboard.assignedTasks': 'Assigned tasks',
        'dashboard.dueToday': 'Due today',
        'dashboard.completedThisWeek': 'Completed this week',
        'dashboard.recentTasks': 'Recent tasks',
        'dashboard.quickActions': 'Quick actions',
        'dashboard.createNewProject': 'Create new project',

        // Errors
        'error.somethingWentWrong': 'Something went wrong',
        'error.notFound': 'Not found',
        'error.unauthorized': 'Unauthorized',
        'error.forbidden': 'Access denied',
        'error.networkError': 'Network error',
        'error.tryAgain': 'Try again',
        'error.projectNotFoundDesc': "The project you're looking for doesn't exist or you don't have access.",

        // Success messages
        'success.saved': 'Saved successfully',
        'success.deleted': 'Deleted successfully',
        'success.created': 'Created successfully',
        'success.updated': 'Updated successfully',
        'success.copied': 'Copied to clipboard',

        // Confirmation dialogs
        'confirm.deleteTask': 'Are you sure you want to delete this task? This action cannot be undone. All subtasks will also be deleted.',
        'confirm.deleteProject': 'Are you sure you want to delete this project?',
        'confirm.deleteComment': 'Are you sure you want to delete this comment?',
        'confirm.deleteAttachment': 'Are you sure you want to delete this attachment?',
        'confirm.deleteView': 'Are you sure you want to delete this saved view?',
        'confirm.leaveProject': 'Are you sure you want to leave this project?',
        'confirm.cannotUndo': 'This action cannot be undone.',
        'confirm.deleteCustomField': 'Are you sure you want to delete this custom field? This will remove the field and its values from all tasks.',

        // Custom fields
        'customFields.title': 'Custom fields',
        'customFields.addField': 'Add field',
        'customFields.editField': 'Edit field',
        'customFields.deleteField': 'Delete field',
        'customFields.fieldName': 'Field name',
        'customFields.fieldType': 'Field type',
        'customFields.required': 'Required',
        'customFields.options': 'Options',
        'customFields.addOption': 'Add option',
        'customFields.text': 'Text',
        'customFields.number': 'Number',
        'customFields.date': 'Date',
        'customFields.select': 'Select',
        'customFields.multiSelect': 'Multi-select',
        'customFields.checkbox': 'Checkbox',
        'customFields.url': 'URL',
        'customFields.email': 'Email',
        'customFields.person': 'Person',
        'customFields.sprint': 'Sprint',
        'customFields.sprintOptionName': 'Sprint name',
        'customFields.sprintOptionStart': 'Start date',
        'customFields.sprintOptionEnd': 'End date',
        'customFields.sprintAddOption': 'Add sprint',
        'customFields.sprintNoOptions': 'No sprints yet. Add one to get started.',

        // Project Settings
        'projectSettings.projectDetails': 'Project details',
        'projectSettings.projectName': 'Project name',
        'projectSettings.description': 'Description',
        'projectSettings.projectIcon': 'Project icon',
        'projectSettings.emoji': 'Emoji',
        'projectSettings.image': 'Image',
        'projectSettings.chooseEmoji': 'Choose Emoji',
        'projectSettings.uploadImage': 'Upload Image',
        'projectSettings.maxSize': 'Max size: 5MB. Supported formats: PNG, JPG, GIF, WebP',
        'projectSettings.color': 'Color',
        'projectSettings.processingImage': 'Processing image...',
        'projectSettings.saveChanges': 'Save changes',
        'projectSettings.dangerZone': 'Danger Zone',
        'projectSettings.dangerZoneDescription': 'Once you delete a project, there is no going back. All tasks, comments, and attachments will be permanently deleted.',
        'projectSettings.teamMembers': 'Team Members',
        'projectSettings.pendingInvitations': 'Pending invitations',
        'projectSettings.inviteTeamMember': 'Invite team member',
        'projectSettings.inviteDescription': "Send an invitation to join this project. They'll receive an email with a link to accept.",
        'projectSettings.joinLinkTitle': 'Invite with a link',
        'projectSettings.joinLinkDescription': 'Anyone with this link can join the project as a member.',
        'projectSettings.createJoinLink': 'Create join link',
        'projectSettings.regenerateJoinLink': 'Generate a new link',
        'projectSettings.disableJoinLink': 'Disable link',
        'projectSettings.joinLinkWarning': 'Generating a new link immediately invalidates the previous one.',
        'projectSettings.integrations': 'Integrations',
        'projectSettings.discordWebhookTitle': 'Discord project channel',
        'projectSettings.discordWebhookDescription': 'Publish team activity to a Discord channel through an incoming webhook.',
        'projectSettings.discordWebhookUrl': 'Discord webhook URL',
        'projectSettings.discordWebhookHint': 'In Discord: Channel settings → Integrations → Webhooks → New webhook, then copy its URL.',
        'projectSettings.connectWebhook': 'Save and test',
        'projectSettings.testWebhook': 'Send a test',
        'projectSettings.disconnectWebhook': 'Disconnect',
        'projectSettings.webhookConnected': 'Discord channel connected',
        'projectSettings.webhookChannel': 'Channel ID',
        'projectSettings.webhookSaved': 'The Discord webhook was saved and the test message was delivered.',
        'projectSettings.webhookRemoved': 'The Discord webhook was disconnected.',
        'projectSettings.webhookTestSent': 'The test message was delivered to Discord.',
        'projectSettings.data': 'Import / Export',
        'projectSettings.transferTitle': 'Task import and export',
        'projectSettings.transferDescription': 'Move or bulk-edit tasks using a versioned JSON file.',
        'projectSettings.exportTitle': 'Export project tasks',
        'projectSettings.exportDescription': 'Downloads custom-field definitions, editable values, assignees and the full nested subtask tree.',
        'projectSettings.exportButton': 'Export JSON',
        'projectSettings.exporting': 'Exporting...',
        'projectSettings.importTitle': 'Import or update tasks',
        'projectSettings.importDescription': 'Existing task IDs are updated; new IDs are created. Missing fields and options are added automatically. Tasks marked "deleted" (or listed in "deletedTasks") are removed.',
        'projectSettings.importButton': 'Choose JSON file',
        'projectSettings.previewingImport': 'Checking file...',
        'projectSettings.importSafetyHint': 'The file is checked before import. Tasks missing from the file are never deleted: only explicit deletions ("deleted": true or "deletedTasks") remove a task, together with its subtasks. The import is applied atomically.',
        'projectSettings.importPreviewTitle': 'Confirm task import',
        'projectSettings.importPreviewDescription': 'Review the changes from {file} before writing them to this project.',
        'projectSettings.tasksToCreate': 'Tasks to create',
        'projectSettings.tasksToUpdate': 'Tasks to update',
        'projectSettings.tasksToDelete': 'Tasks to delete',
        'projectSettings.fieldsToCreate': 'Fields to create',
        'projectSettings.fieldsToUpdate': 'Fields to update',
        'projectSettings.optionsToAdd': 'Options to add',
        'projectSettings.importWarnings': 'Warnings',
        'projectSettings.confirmImport': 'Import changes',
        'projectSettings.importing': 'Importing...',
        'projectSettings.importSuccess': 'Import completed',
        'projectSettings.importSuccessDescription': '{created} task(s) created, {updated} updated and {deleted} deleted.',
        'projectSettings.invalidImportFile': 'This JSON file is not a valid Project Manager task export.',
        'projectSettings.importFileTooLarge': 'The import file must be smaller than 25 MB.',
        'projectSettings.emailAddress': 'Email address',
        'projectSettings.emailPlaceholder': 'colleague@example.com',
        'projectSettings.role': 'Role',
        'projectSettings.roleAdminDesc': 'Admins can manage members and project settings.',
        'projectSettings.roleMemberDesc': 'Members can create and edit tasks, comment, and upload files.',
        'projectSettings.roleViewerDesc': 'Viewers have read-only access to the project.',
        'projectSettings.sendInvitation': 'Send invitation',
        'projectSettings.copyLink': 'Copy link',
        'projectSettings.youIndicator': '(you)',
        'projectSettings.projectNotFound': 'Project not found',
        'projectSettings.backToProjects': 'Back to projects',
        'projectSettings.deleteProjectTitle': 'Delete project',
        'projectSettings.deleteProjectDescription': 'Are you sure you want to delete "{name}"? This action cannot be undone. All tasks, comments, and attachments will be permanently deleted.',

        // Invite Links (R14 t46)
        'inviteLink.sectionTitle': 'Invite links',
        'inviteLink.sectionDescription': 'Create authenticated invite links. Each link adds a user as Member or Viewer; revoke any time.',
        'inviteLink.create': 'Create link',
        'inviteLink.createFirst': 'Create the first link',
        'inviteLink.createTitle': 'Create invite link',
        'inviteLink.createDescription': 'Pick the role and an optional expiry. Sign-in is required to redeem.',
        'inviteLink.role': 'Role',
        'inviteLink.role.MEMBER': 'Member',
        'inviteLink.role.MEMBER.description': 'Can create and edit tasks, comment, upload files.',
        'inviteLink.role.VIEWER': 'Viewer',
        'inviteLink.role.VIEWER.description': 'Read-only access to tasks, views and comments.',
        'inviteLink.hasExpiry': 'Set an expiry date',
        'inviteLink.expiresAt': 'Expires {date}',
        'inviteLink.empty': 'No invite links yet.',
        'inviteLink.copy': 'Copy',
        'inviteLink.copied': 'Copied',
        'inviteLink.copyFailed': 'Could not copy to clipboard.',
        'inviteLink.revoke': 'Revoke',
        'inviteLink.revoked': 'Invite link revoked.',
        'inviteLink.created': 'Invite link created.',
        'inviteLink.createdBy': 'Created by {name} on {date}',
        'inviteLink.status.active': 'Active',
        'inviteLink.status.revoked': 'Revoked',
        'inviteLink.status.expired': 'Expired',
        'inviteLink.title': "You're invited",
        'inviteLink.subtitle': 'You\'ve been invited to join "{name}"',
        'inviteLink.invitedBy': 'Invited by {name}',
        'inviteLink.accept': 'Accept invitation',
        'inviteLink.decline': 'Maybe later',
        'inviteLink.authenticatedNotice': 'Sign-in required to accept.',
        'inviteLink.acceptSuccess': 'Welcome aboard!',
        'inviteLink.acceptSuccessDescription': 'You\'ve joined {name}.',
        'inviteLink.acceptFailed': 'Could not redeem the invite link.',
        'inviteLink.invalid': 'Invite link is invalid',
        'inviteLink.invalidDescription': 'This link has been revoked, expired, or never existed.',
        'inviteLink.backToProjects': 'Back to projects',

        // Toast messages
        'toast.invalidFileType': 'Invalid file type',
        'toast.fileTooLarge': 'File too large',
        'toast.uploadFailed': 'Upload failed',
        'toast.settingsSaved': 'Settings saved',
        'toast.settingsSavedDesc': 'Project settings have been updated.',
        'toast.invitationSent': 'Invitation sent',
        'toast.invitationSentDesc': 'An invitation has been created for {email}.',
        'toast.projectDeleted': 'Project deleted',
        'toast.projectDeletedDesc': 'The project has been permanently deleted.',
        'toast.roleUpdated': 'Role updated',
        'toast.roleUpdatedDesc': 'Member role has been updated.',
        'toast.memberRemoved': 'Member removed',
        'toast.memberRemovedDesc': 'The member has been removed from the project.',
        'toast.invitationCancelled': 'Invitation cancelled',
        'toast.invitationCancelledDesc': 'The invitation has been cancelled.',
        'toast.linkCopied': 'Link copied',
        'toast.linkCopiedDesc': 'Invitation link has been copied to clipboard.',

        // Table columns
        'table.selection': 'Selection',
        'table.task': 'Task',
        'table.actions': 'Actions',
        'table.horizontalScroll': 'Scroll task fields horizontally',
        'tasks.paginationShowing': 'Showing',
        'tasks.paginationOf': 'of',
        'tasks.loadMore': 'Load more tasks',

        // Task Dialog
        'taskDialog.createTitle': 'Create new task',
        'taskDialog.editTitle': 'Edit task',
        'taskDialog.createDescription': 'Fill in the details to create a new task.',
        'taskDialog.editDescription': 'Update the task details below.',
        'taskDialog.title': 'Title',
        'taskDialog.titleRequired': 'Task title is required',
        'taskDialog.titlePlaceholder': 'Enter task title...',
        'taskDialog.description': 'Description',
        'taskDialog.descriptionPlaceholder': 'Add a description...',
        'taskDialog.selectStatus': 'Select status',
        'taskDialog.selectPriority': 'Select priority',
        'taskDialog.noTeamMembers': 'No team members available',
        'taskDialog.saveChanges': 'Save changes',
        'taskDialog.createTask': 'Create task',
        'taskDialog.owner': 'Owner',
        'taskDialog.customFields': 'Custom fields',
        'taskDialog.selectPlaceholder': 'Select...',

        // Create Project Dialog
        'createProject.title': 'Create new project',
        'createProject.description': 'Create a new project to organize your tasks and collaborate with your team.',
        'createProject.projectName': 'Project name',
        'createProject.projectNamePlaceholder': 'Enter project name',
        'createProject.projectDescription': 'Description',
        'createProject.projectDescriptionPlaceholder': 'Describe what this project is about',
        'createProject.descriptionOptional': '(optional)',
        'createProject.projectIcon': 'Project icon',
        'createProject.emoji': 'Emoji',
        'createProject.image': 'Image',
        'createProject.chooseEmoji': 'Choose emoji',
        'createProject.uploadImage': 'Upload image',
        'createProject.maxSizeHint': 'Max size: 5MB. Supported formats: PNG, JPG, GIF, WebP',
        'createProject.color': 'Color',
        'createProject.preview': 'Preview',
        'createProject.processingImage': 'Processing image...',
        'createProject.createButton': 'Create project',
        'createProject.newProject': 'New project',
        'createProject.success': 'Project created',
        'createProject.successDesc': '"{name}" has been created successfully.',
        'createProject.uploadFailedDesc': 'Failed to process project image. The project will be created without an image.',

        // Project Card Actions
        'projectCard.settings': 'Settings',
        'projectCard.archive': 'Archive',
        'projectCard.unarchive': 'Unarchive',
        'projectCard.delete': 'Delete',
        'projectCard.leaveProject': 'Leave project',
        'projectCard.archived': 'Archived',
        'projectCard.tasks': 'tasks',

        // Settings page additional
        'settings.emailCannotBeChanged': 'Email cannot be changed',
        'settings.chooseNotifications': 'Choose which notifications you want to receive',
        'settings.linkDiscordDesc': 'Link your Discord to receive notifications',
        'settings.uploadProfilePictureHint': 'Upload a profile picture (max 5MB)',

        // Toast messages for tasks
        'toast.taskCreated': 'Task created',
        'toast.taskCreatedDesc': 'Task has been created successfully.',
        'toast.taskUpdated': 'Task updated',
        'toast.taskUpdatedDesc': 'Task has been updated successfully.',
        'toast.taskDeleted': 'Task deleted',
        'toast.taskDeletedDesc': 'Task has been deleted successfully.',
        'toast.tasksDeleted': 'Tasks deleted',
        'toast.tasksDeletedDesc': '{count} task(s) deleted successfully.',
        'toast.statusUpdated': 'Status updated',
        'toast.statusUpdatedDesc': '{count} task(s) updated.',
        'toast.priorityUpdated': 'Priority updated',
        'toast.priorityUpdatedDesc': '{count} task(s) updated.',
        'toast.assigneesUpdated': 'Assignees updated',
        'toast.assigneesUpdatedDesc': 'Assignee added to {count} task(s).',
        'toast.viewSaved': 'View saved',
        'toast.viewSavedDesc': 'Your view changes have been saved.',
        'toast.viewDeleted': 'View deleted',
        'toast.errorSavingView': 'Failed to save view changes.',
        'toast.errorDeletingTasks': 'Failed to delete some tasks.',
        'toast.errorUpdatingTasks': 'Failed to update some tasks.',
        'toast.subtaskCreated': 'Subtask created',
        'toast.subtaskCreatedDesc': 'New subtask has been added.',
        'toast.dueDateUpdated': 'Due date updated',
        'toast.dueDateSet': 'Due date has been set.',
        'toast.dueDateCleared': 'Due date has been cleared.',
        'toast.fieldDeleted': 'Field deleted',
        'toast.fieldDeletedDesc': 'Custom field has been deleted.',
        'toast.errorDeletingField': 'Failed to delete custom field.',
        'toast.errorAssignees': 'Failed to update assignees.',
        'toast.errorDueDate': 'Failed to update due date.',
        'toast.errorCustomField': 'Failed to update custom field.',
        'toast.errorTaskAction': 'Failed to {action} the task. Please try again.',

        // Custom field dialog
        'customFields.editFieldTitle': 'Edit field',
        'customFields.addFieldTitle': 'Add custom field',
        'customFields.editFieldDescription': 'Update the custom field settings.',
        'customFields.addFieldDescription': 'Create a new custom field for your tasks.',
        'customFields.fieldNamePlaceholder': 'e.g., Category, Sprint, etc.',
        'customFields.saving': 'Saving...',
        'customFields.createField': 'Create field',
        'customFields.noFieldsYet': 'No custom fields yet.',
        'customFields.noFieldsHint': 'Add one to get started.',
        'customFields.optionsCount': '{count} options',
        'customFields.deleteFieldTitle': 'Delete custom field',
        'customFields.deleteFieldDescription': 'Are you sure you want to delete "{name}"? This will remove the field and its values from all tasks in this project.',
        'customFields.deleting': 'Deleting...',

        // Saved views panel
        'views.deleteViewTitle': 'Delete view',
        'views.deleteViewConfirmation': 'Are you sure you want to delete this saved view? This action cannot be undone.',

        // Landing page
        'landing.heroTitle': 'Project management,',
        'landing.heroSubtitle': 'reimagined',
        'landing.getStarted': 'Get started',
        'landing.tryDemo': 'Try interactive demo',
        'landing.feature.projects': 'Organize projects',
        'landing.feature.projectsDesc': 'Create and manage unlimited projects with custom icons, colors, and team permissions.',
        'landing.feature.team': 'Team collaboration',
        'landing.feature.teamDesc': 'Invite team members, assign roles, and work together seamlessly.',
        'landing.feature.realtime': 'Real-time updates',
        'landing.feature.realtimeDesc': 'See changes instantly as your team works. No refresh needed.',
        'landing.feature.comments': 'Comments & attachments',
        'landing.feature.commentsDesc': 'Discuss tasks with @mentions and attach files directly to tasks.',
        'landing.feature.views': 'Custom views',
        'landing.feature.viewsDesc': 'Save views with your own filters and customize columns, allowing you to organize your workspace according to your preferences.',
        'landing.feature.themes': 'Multiple themes',
        'landing.feature.themesDesc': 'Choose from stunning animated themes with interactive particle effects.',
        'landing.feature.notifications': 'Smart notifications',
        'landing.feature.notificationsDesc': 'Stay updated with in-app and Discord notifications.',
        'landing.feature.customFields': 'Custom columns',
        'landing.feature.customFieldsDesc': 'Add text, numbers, dates, dropdowns, and more to your tasks.',
        'landing.demo.title': 'Experience it yourself',
        'landing.demo.subtitle': 'Try changing themes and interact with the demo below',
        'landing.demo.tryTheme': 'Choose a theme',
        'landing.demo.taskDemo': 'Interactive task Demo',
        'landing.demo.interactiveDemo': 'Click on tasks, change status, and explore!',
        'landing.cta.title': 'Ready to get organized?',
        'landing.cta.subtitle': 'Start managing your projects with style today.',
        'landing.scrollToExplore': 'Scroll to explore',
        'landing.builtWith': 'Built with',
        'landing.selfHosted': 'Free forever',
        'landing.selfHostedDesc': 'No credit card required. Ever.',
        'landing.openSource': 'Open source',
        'landing.freeForAll': 'Free for everyone',
        'landing.freeForAllDesc': 'No hidden fees, no limits. Use all features for free.',
        'landing.hostedForYou': 'Hosted for you',
        'landing.hostedForYouDesc': 'We handle the servers, you focus on your projects.',
    },
    fr: {
        // Common
        'common.save': 'Enregistrer',
        'common.saveChanges': 'Enregistrer les modifications',
        'common.cancel': 'Annuler',
        'common.delete': 'Supprimer',
        'common.edit': 'Modifier',
        'common.create': 'Créer',
        'common.add': 'Ajouter',
        'common.remove': 'Retirer',
        'common.search': 'Rechercher',
        'common.filter': 'Filtrer',
        'common.loading': 'Chargement...',
        'common.noData': 'Aucune donnée',
        'common.error': 'Erreur',
        'common.success': 'Succès',
        'common.confirm': 'Confirmer',
        'common.close': 'Fermer',
        'common.yes': 'Oui',
        'common.no': 'Non',
        'common.none': 'Aucun',
        'common.all': 'Tout',
        'common.clear': 'Effacer',
        'common.actions': 'Actions',
        'common.settings': 'Paramètres',
        'common.name': 'Nom',
        'common.email': 'E-mail',
        'common.password': 'Mot de passe',
        'common.submit': 'Soumettre',
        'common.back': 'Retour',
        'common.next': 'Suivant',
        'common.previous': 'Précédent',
        'common.today': "Aujourd'hui",
        'common.tomorrow': 'Demain',
        'common.yesterday': 'Hier',
        'common.overdue': 'En retard',
        'common.unassigned': 'Non assigné',
        'common.viewAll': 'Voir tout',

        // Navigation
        'nav.home': 'Tableau de bord',
        'nav.dashboard': 'Tableau de bord',
        'nav.projects': 'Projets',
        'nav.myTasks': 'Mes tâches',
        'nav.notifications': 'Notifications',
        'nav.settings': 'Paramètres',
        'nav.signOut': 'Se déconnecter',
        'nav.favorites': 'Favoris',

        // Auth
        'auth.login': 'Connexion',
        'auth.register': "S'inscrire",
        'auth.signIn': 'Se connecter',
        'auth.signUp': "S'inscrire",
        'auth.logout': 'Déconnexion',
        'auth.forgotPassword': 'Mot de passe oublié ?',
        'auth.resetPassword': 'Réinitialiser le mot de passe',
        'auth.forgotPasswordTitle': 'Mot de passe oublié ?',
        'auth.forgotPasswordDescription': 'Saisissez votre adresse e-mail et nous vous enverrons un lien sécurisé.',
        'auth.sendResetLink': 'Envoyer le lien',
        'auth.sendingResetLink': 'Envoi du lien…',
        'auth.resetEmailSent': 'Si un compte existe pour cette adresse, un lien de réinitialisation a été envoyé.',
        'auth.backToLogin': 'Retour à la connexion',
        'auth.resetPasswordDescription': 'Choisissez un nouveau mot de passe pour votre compte.',
        'auth.confirmNewPassword': 'Confirmer le nouveau mot de passe',
        'auth.resettingPassword': 'Réinitialisation…',
        'auth.passwordResetSuccess': 'Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter.',
        'auth.checkYourEmailTitle': 'Confirmez votre adresse e-mail',
        'auth.checkYourEmailBody': 'Nous vous avons envoyé un lien de vérification.',
        'auth.checkYourEmailHint': 'Cliquez sur le lien reçu par e-mail pour activer votre compte. Pensez à vérifier vos courriers indésirables.',
        'auth.verifyingEmail': 'Vérification de votre adresse e-mail…',
        'auth.emailVerifiedTitle': 'Adresse e-mail confirmée',
        'auth.emailVerifiedBody': 'Votre compte est maintenant actif. Bienvenue !',
        'auth.goToDashboard': 'Accéder à mon tableau de bord',
        'auth.verifyEmailFailedTitle': 'Échec de la vérification',
        'auth.verifyEmailFailedBody': 'Ce lien de vérification est invalide ou a expiré.',
        'auth.resendVerification': 'Renvoyer le lien de vérification',
        'auth.resendVerificationHint': 'Saisissez votre adresse e-mail pour recevoir un nouveau lien.',
        'auth.verificationResentTitle': 'Lien envoyé',
        'auth.verificationResentBody': 'Si un compte non vérifié existe pour cette adresse, un nouveau lien vient de partir.',
        'auth.loginUnverifiedBody': "Ce compte n'a pas encore été confirmé. Ouvrez le lien envoyé par e-mail ou demandez-en un nouveau.",
        'auth.backToSignIn': 'Retour à la connexion',
        'auth.sending': 'Envoi…',
        'auth.invalidResetLink': 'Ce lien de réinitialisation est invalide ou a expiré.',
        'auth.rememberMe': 'Se souvenir de moi pendant 30 jours',
        'auth.noAccount': "Vous n'avez pas de compte ?",
        'auth.hasAccount': 'Vous avez déjà un compte ?',
        'auth.haveAccount': 'Vous avez déjà un compte ?',
        'auth.emailPlaceholder': 'vous@exemple.com',
        'auth.passwordPlaceholder': 'Entrez votre mot de passe',
        'auth.namePlaceholder': 'Jean Dupont',
        'auth.confirmPassword': 'Confirmer le mot de passe',
        'auth.currentPassword': 'Mot de passe actuel',
        'auth.newPassword': 'Nouveau mot de passe',
        'auth.invalidCredentials': 'E-mail ou mot de passe invalide',
        'auth.emailRequired': "L'e-mail est requis",
        'auth.passwordRequired': 'Le mot de passe est requis',
        'auth.nameRequired': 'Le nom est requis',
        'auth.passwordMinLength': 'Le mot de passe doit contenir au moins 8 caractères',
        'auth.passwordsDoNotMatch': 'Les mots de passe ne correspondent pas',
        'auth.welcomeBack': 'Bon retour',
        'auth.createAccount': 'Créer un compte',
        'auth.createAccountBtn': 'Créer un compte',
        'auth.creatingAccount': 'Création du compte...',
        'auth.getStarted': 'Commencez avec Project Manager',
        'auth.signInToAccount': 'Connectez-vous à votre compte Project Manager',
        'auth.signingIn': 'Connexion en cours...',
        'auth.email': 'E-mail',
        'auth.password': 'Mot de passe',
        'auth.name': 'Nom',
        'auth.password8Chars': 'Au moins 8 caractères',
        'auth.passwordUppercase': 'Une lettre majuscule',
        'auth.passwordLowercase': 'Une lettre minuscule',
        'auth.passwordNumber': 'Un chiffre',
        // OAuth
        'auth.continueWithGoogle': 'Continuer avec Google',
        'auth.continueWithDiscord': 'Continuer avec Discord',
        'auth.orContinueWith': 'ou continuer par e-mail',
        'auth.oauthDenied': 'Accès refusé. Veuillez réessayer.',
        'auth.oauthInvalidRequest': 'Requête invalide. Veuillez réessayer.',
        'auth.oauthInvalidState': 'État invalide. Veuillez réessayer.',
        'auth.oauthTokenFailed': 'Échec de l\'authentification. Veuillez réessayer.',
        'auth.oauthUserFetchFailed': 'Impossible de récupérer vos informations de compte.',
        'auth.oauthEmailRequired': 'Une adresse e-mail est requise pour l\'inscription.',
        'auth.oauthServerError': 'Une erreur s\'est produite. Veuillez réessayer plus tard.',

        // Projects
        'projects.title': 'Projets',
        'projects.createProject': 'Créer un projet',
        'projects.newProject': 'Nouveau projet',
        'projects.editProject': 'Modifier le projet',
        'projects.deleteProject': 'Supprimer le projet',
        'projects.archiveProject': 'Archiver le projet',
        'projects.unarchiveProject': 'Désarchiver le projet',
        'projects.leaveProject': 'Quitter le projet',
        'projects.projectName': 'Nom du projet',
        'projects.projectDescription': 'Description',
        'projects.projectColor': 'Couleur',
        'projects.noProjects': 'Aucun projet pour le moment',
        'projects.searchProjects': 'Rechercher des projets...',
        'projects.showArchived': 'Afficher les archivés',
        'projects.showActive': 'Afficher les actifs',
        'projects.hideArchived': 'Masquer les archivés',
        'projects.members': 'Membres',
        'projects.tasks': 'Tâches',
        'projects.invite': 'Inviter',
        'projects.inviteMembers': 'Inviter des membres',
        'projects.inviteSent': 'Invitation envoyée',
        'projects.projectSettings': 'Paramètres du projet',
        'projects.dangerZone': 'Zone de danger',
        'projects.deleteWarning': 'Cette action est irréversible. Cela supprimera définitivement le projet et toutes ses données.',
        'projects.owner': 'Propriétaire',
        'projects.admin': 'Administrateur',
        'projects.member': 'Membre',
        'projects.viewer': 'Lecteur',
        'projects.manageProjects': 'Gérez vos projets et collaborez avec votre équipe',
        'projects.noProjectsFound': 'Aucun projet trouvé',
        'projects.noArchivedProjects': 'Aucun projet archivé',
        'projects.tryDifferentSearch': 'Essayez un autre terme de recherche',
        'projects.archiveToSeeHere': 'Archivez des projets pour les voir ici',
        'projects.createFirstProject': 'Créez votre premier projet pour commencer',
        'projects.deleteConfirmation': 'Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible. Toutes les tâches, commentaires et pièces jointes seront définitivement supprimés.',
        'projects.leaveConfirmation': 'Êtes-vous sûr de vouloir quitter',
        'projects.currentProject': 'Projet actuel',
        'projects.manageTeam': 'Gérer l\'équipe',
        'projects.general': 'Général',
        'projects.teamMembers': 'Membres de l\'équipe',
        'projects.customization': 'Personnalisation',
        'projects.projectIcon': 'Icône du projet',
        'projects.useEmoji': 'Utiliser un emoji',
        'projects.useImage': 'Utiliser une image',
        'projects.selectEmoji': 'Choisir un emoji',
        'projects.uploadImage': 'Téléverser une image',
        'projects.changeImage': 'Changer l\'image',
        'projects.removeImage': 'Supprimer',
        'projects.imageHint': 'PNG, JPG, GIF jusqu\'à 5 Mo',
        'projects.inviteEmail': 'Adresse e-mail',
        'projects.role': 'Rôle',
        'projects.expires': 'Expire',
        'projects.expiresInDays': 'Expire dans',
        'projects.day': 'jour',
        'projects.days': 'jours',
        'projects.pendingInvitations': 'Invitations en attente',
        'projects.cancelInvitation': 'Annuler l\'invitation',
        'projects.copyLink': 'Copier le lien d\'invitation',
        'projects.linkCopied': 'Lien copié dans le presse-papiers',
        'projects.removeMember': 'Retirer du projet',
        'projects.changeRole': 'Changer le rôle',
        'projects.noMembers': 'Aucun membre pour le moment',
        'projects.inviteDescription': 'Invitez des membres à collaborer sur ce projet',
        'projects.invalidFileType': 'Veuillez sélectionner un fichier image',
        'projects.fileTooLarge': 'Veuillez sélectionner une image de moins de 5 Mo',

        // Project aliases
        'project.createProject': 'Créer un projet',
        'project.newProject': 'Nouveau projet',
        'project.manageProjects': 'Gérez vos projets et collaborez avec votre équipe',
        'project.searchProjects': 'Rechercher des projets...',
        'project.showActive': 'Afficher les actifs',
        'project.showArchived': 'Afficher les archivés',
        'project.noProjectsFound': 'Aucun projet trouvé',
        'project.noArchivedProjects': 'Aucun projet archivé',
        'project.noProjects': 'Aucun projet pour le moment',
        'project.tryDifferentSearch': 'Essayez un autre terme de recherche',
        'project.archiveToSeeHere': 'Archivez des projets pour les voir ici',
        'project.createFirstProject': 'Créez votre premier projet pour commencer',
        'project.deleteProject': 'Supprimer le projet',
        'project.leaveProject': 'Quitter le projet',
        'project.deleteConfirmation': 'Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible. Toutes les tâches, commentaires et pièces jointes seront définitivement supprimés.',
        'project.leaveConfirmation': 'Êtes-vous sûr de vouloir quitter',

        // Tasks
        'tasks.title': 'Tâches',
        'tasks.myTasks': 'Mes tâches',
        'tasks.createTask': 'Créer une tâche',
        'tasks.newTask': 'Nouvelle tâche',
        'tasks.editTask': 'Modifier la tâche',
        'tasks.deleteTask': 'Supprimer la tâche',
        'tasks.taskName': 'Nom de la tâche',
        'tasks.taskDescription': 'Description',
        'tasks.noTasks': 'Aucune tâche pour le moment',
        'tasks.noTasksDescription': 'Créez une tâche pour commencer',
        'tasks.addTask': 'Ajouter une tâche',
        'tasks.addSubtask': 'Ajouter une sous-tâche',
        'tasks.taskDetails': 'Détails de la tâche',
        'tasks.resizePanel': 'Redimensionner le panneau de détails',
        'tasks.subtasks': 'Sous-tâches',
        'tasks.dependsOn': 'Dépend de',
        'tasks.dependsOnEmpty': 'Aucune dépendance pour l\'instant.',
        'tasks.dependsOnNoMatches': 'Aucune tâche ne correspond.',
        'tasks.dependsOnSearchPlaceholder': 'Rechercher une tâche à ajouter comme dépendance...',
        'tasks.assignees': 'Assignés',
        'tasks.assignTo': 'Assigner à',
        'tasks.selectAssignees': 'Sélectionner les assignés',
        'tasks.dueDate': 'Date limite',
        'tasks.createdAt': 'Créé le',
        'tasks.customFieldsSection': 'Champs personnalisés',
        'tasks.customFieldAdd': '+ Ajouter',
        'tasks.customFieldNone': 'Aucun',
        'tasks.setDueDate': 'Définir la date limite',
        'tasks.startDate': 'Date de début',
        'tasks.endDate': 'Date de fin',
        'tasks.notSet': 'Non définie',
        'tasks.clearDate': 'Effacer la date',
        'tasks.status': 'Statut',
        'tasks.priority': 'Priorité',
        'tasks.comments': 'Commentaires',
        'tasks.attachments': 'Pièces jointes',
        'tasks.task': 'Tâche',
        'tasks.selected': 'sélectionnée(s)',
        'tasks.setStatus': 'Définir le statut',
        'tasks.setPriority': 'Définir la priorité',
        'tasks.noMatchingTasks': 'Aucune tâche correspondante',
        'tasks.tryAdjustingFilters': 'Essayez d\'ajuster vos filtres pour trouver des tâches.',
        'tasks.getStartedCreatingTask': 'Commencez par créer votre première tâche pour ce projet.',
        'tasks.createFirstTask': 'Créer votre première tâche',
        'tasks.assign': 'Assigner',
        'tasks.customFields': 'Champs personnalisés',
        'tasks.columns': 'Colonnes',
        'tasks.noTasksFound': 'Aucune tâche trouvée.',
        'tasks.enterTitle': 'Entrez le titre de la tâche...',
        'tasks.enterSubtaskTitle': 'Entrez le titre de la sous-tâche...',
        'tasks.clearValue': 'Effacer la valeur',
        'tasks.editIndividually': 'Modifier individuellement',
        'tasks.toggleOptions': 'Basculer les options pour les tâches sélectionnées',
        'tasks.checked': 'Coché',
        'tasks.unchecked': 'Non coché',
        'tasks.deleteTaskAction': 'Supprimer la tâche',
        'tasks.loadingSubtasks': 'Chargement des sous-tâches...',
        'tasks.editTitle': 'Modifier le titre',

        // Task aliases
        'task.today': "Aujourd'hui",
        'task.tomorrow': 'Demain',
        'task.overdue': 'En retard',
        'task.noTasks': 'Aucune tâche',
        'task.noTasksDescription': 'Aucune tâche ne correspond à vos filtres',

        // Task Status
        'status.todo': 'À faire',
        'status.inProgress': 'En cours',
        'status.inReview': 'En revue',
        'status.done': 'Terminé',
        'status.cancelled': 'Annulé',

        // Task Priority
        'priority.none': 'Aucune',
        'priority.low': 'Basse',
        'priority.medium': 'Moyenne',
        'priority.high': 'Haute',
        'priority.urgent': 'Urgente',

        // Views
        'views.savedViews': 'Vues sauvegardées',
        'views.saveView': 'Sauvegarder la vue',
        'views.editView': 'Modifier',
        'views.editViewTitle': 'Modifier la vue',
        'views.deleteView': 'Supprimer',
        'views.duplicateView': 'Dupliquer',
        'views.setDefault': 'Définir par défaut',
        'views.setAsDefault': 'Définir comme vue par défaut',
        'views.shareWithTeam': "Partager avec l'équipe",
        'views.viewName': 'Nom de la vue',
        'views.viewNamePlaceholder': 'ex: Mes tâches, haute priorité',
        'views.saveViewDescription': 'Sauvegarder la configuration actuelle des filtres et colonnes.',
        'views.editViewDescription': 'Mettre à jour les paramètres de la vue.',
        'views.allTasks': 'Toutes les tâches',
        'views.noViews': 'Aucune vue sauvegardée. Configurez les filtres et sauvegardez votre vue.',
        'views.isDefault': 'Définir comme vue par défaut',
        'views.isShared': "Partager avec l'équipe",
        'views.saveCurrentView': 'Sauvegarder la configuration actuelle des filtres et colonnes.',
        'views.favorites': 'Favoris',
        'views.personal': 'Personnel',
        'views.shared': 'Partagé',
        'views.noFavorites': 'Aucune vue favorite',
        'views.noPersonalViews': 'Aucune vue personnelle',
        'views.noSharedViews': 'Aucune vue partagée',
        'views.addToFavorites': 'Ajouter aux favoris',
        'views.removeFromFavorites': 'Retirer des favoris',
        'views.isFavorite': 'Est favori',

        // View type toggle
        'viewType.table': 'Tableau',
        'viewType.kanban': 'Kanban',
        'viewType.calendar': 'Calendrier',
        'viewType.gantt': 'Gantt',
        'viewType.document': 'Document',
        'viewType.toggleAriaLabel': 'Basculer entre les vues tableau, kanban, calendrier, gantt et document',

        // R14: Document mode (Français)
        'document.defaultTabName': 'Général',
        'document.newTab': 'Nouvel onglet',
        'document.closeTab': "Fermer l'onglet",
        'document.tabAriaLabel': 'Onglet de document',
        'document.tabsAriaLabel': 'Onglets de document',
        'document.emptyTitle': 'Aucun document',
        'document.emptyBody': 'Créez un nouveau document pour commencer à écrire. Les documents se synchronisent en direct entre collaborateurs et sont enregistrés automatiquement.',
        'document.emptyCreateCta': 'Créer un document',
        'document.readOnlyBadge': 'Lecture seule',
        'document.loading': 'Chargement du document…',
        'document.connecting': 'Connexion au serveur de collaboration…',
        'document.connected': 'En direct',
        'document.disconnected': 'Hors ligne',
        'document.saveStatus.saving': 'Enregistrement…',
        'document.saveStatus.saved': 'Enregistré',
        'document.saveStatus.error': "Échec de l'enregistrement",
        'document.saveStatus.dirty': 'Modifications non enregistrées',
        'document.saveStatus.errorHint':
            "Vos modifications n'ont pas pu être envoyées. Elles restent non enregistrées — nouvel essai automatique.",
        'document.lastSavedAt': 'Dernier enregistrement {time}',
        'document.collab.peers': '{count} autres éditeurs',
        'document.collab.onePeer': '1 autre éditeur',
        'document.collab.zeroPeers': 'Vous seul',
        'document.toolbar.bold': 'Gras',
        'document.toolbar.italic': 'Italique',
        'document.toolbar.underline': 'Souligné',
        'document.toolbar.h1': 'Titre 1',
        'document.toolbar.h2': 'Titre 2',
        'document.toolbar.h3': 'Titre 3',
        'document.toolbar.bulletList': 'Liste à puces',
        'document.toolbar.orderedList': 'Liste numérotée',
        'document.toolbar.blockquote': 'Citation',
        'document.toolbar.paragraph': 'Paragraphe',
        'document.toolbar.fontFamily': 'Police',
        'document.toolbar.fontSize': 'Taille',
        'document.toolbar.fontFamily.sans': 'Sans serif',
        'document.toolbar.fontFamily.arial': 'Arial',
        'document.toolbar.fontFamily.serif': 'Georgia (Serif)',
        'document.toolbar.fontFamily.times': 'Times New Roman',
        'document.toolbar.fontFamily.verdana': 'Verdana',
        'document.toolbar.fontFamily.courier': 'Courier New',
        'document.toolbar.fontFamily.mono': 'Monospace système',
        'document.toolbar.fontFamily.comic': 'Comic Sans',
        'document.toolbar.fontFamily.default': 'Police par défaut',
        'document.toolbar.fontSize.small': 'Petit',
        'document.toolbar.fontSize.medium': 'Moyen',
        'document.toolbar.fontSize.large': 'Grand',
        'document.toolbar.fontSize.xs': 'Très petit',
        'document.toolbar.fontSize.xl': 'Très grand',
        'document.toolbar.fontSize.xxl': 'Énorme',
        'document.toolbar.fontSize.default': 'Taille par défaut',
        'document.toolbar.fontSizeCustomHint': 'Définir une taille exacte en pixels (6–200)',
        'document.toolbar.color': 'Couleur du texte',
        'document.toolbar.colorCustom': 'Personnalisée',
        'document.toolbar.colorClear': 'Effacer la couleur',
        'document.toolbar.tocTitle': 'Table des matières',
        'document.toolbar.tocEmpty': 'Aucun titre — ajoutez un titre pour remplir la table.',
        'document.toolbar.tocUntitled': '(sans titre)',
        'document.toolbar.insertTable': 'Insérer un tableau',
        'document.toolbar.addRowBefore': 'Insérer une ligne au-dessus',
        'document.toolbar.addColumnBefore': 'Insérer une colonne à gauche',
        'document.toolbar.mergeCells': 'Fusionner les cellules',
        'document.toolbar.splitCell': 'Scinder la cellule',
        'document.toolbar.toggleHeaderRow': 'Basculer la ligne d’en-tête',
        'document.toolbar.toggleHeaderColumn': 'Basculer la colonne d’en-tête',
        'document.toolbar.addRowAfter': 'Insérer une ligne en dessous',
        'document.toolbar.addColumnAfter': 'Insérer une colonne à droite',
        'document.toolbar.deleteRow': 'Supprimer la ligne',
        'document.toolbar.deleteColumn': 'Supprimer la colonne',
        'document.toolbar.deleteTable': 'Supprimer le tableau',
        'document.toolbar.undo': 'Annuler',
        'document.toolbar.redo': 'Rétablir',
        // R20: text alignment (left / center / right / justify)
        'document.toolbar.alignLeft': 'Aligner à gauche',
        'document.toolbar.alignCenter': 'Centrer',
        'document.toolbar.alignRight': 'Aligner à droite',
        'document.toolbar.alignJustify': 'Justifier',
        'document.toolbar.alignLeftHint': 'Aligner le paragraphe à gauche',
        'document.toolbar.alignCenterHint': 'Centrer le paragraphe',
        'document.toolbar.alignRightHint': 'Aligner le paragraphe à droite',
        'document.toolbar.alignJustifyHint': 'Justifier le paragraphe',
        // R18 (t59): export-to-PDF / DOCX button in the toolbar.
        'document.exportMenu': 'Exporter',
        'document.exportPdf': 'Exporter en PDF',
        'document.exportDocx': 'Exporter en DOCX',
        'document.exportTitle': 'Télécharger le document',
        'document.editorPlaceholder': 'Commencez à taper — ou appuyez sur / pour les commandes',
        'document.deleteViewConfirm': 'Supprimer ce document ?',
        'document.tableHint': 'Astuce : tapez +table=3x4 + espace pour insérer un tableau 3×4.',
        // R14: @-mention picker (Français)
        'document.mention.loading': 'Chargement des tâches…',
        'document.mention.empty': 'Aucune tâche correspondante',
        'document.mention.pickerAriaLabel': 'Mentionner une tâche ou une personne',
        'document.mention.task': 'Tâche',
        'document.mention.person': 'Personne',
        'document.mention.searchPlaceholder': 'Rechercher des tâches ou personnes…',
        'document.mention.searchAriaLabel': 'Rechercher des mentions',
        'document.mention.section.tasks': 'Tâches',
        'document.mention.section.people': 'Personnes',
        'document.mention.notified': 'Vous avez mentionné {name}',
        'document.mention.notifiedTitle': 'Mention envoyée',
        'document.mention.emptyAfterSearch': 'Aucun résultat pour « {query} »',
        // R25 : recherche serveur (bug A) + popover de mention de personne (bug B)
        'document.mention.searchUnavailable': 'Recherche indisponible — tâches chargées affichées',
        'document.mention.loadMore': 'Charger plus',
        'document.mention.personTasksTitle': 'Tâches assignées',
        'document.mention.personTasksEmpty': 'Aucune tâche assignée à cette personne',
        'document.mention.personTasksError': 'Impossible de charger les tâches',
        'calendar.comingSoonTitle': 'Vue calendrier à venir',
        'calendar.comingSoonBody': 'La vue Calendrier affichera les tâches sur une grille mois / semaine / agenda. En attendant, continuez avec Tableau ou Kanban.',
        'calendar.activeViewLabel': 'Vue active',
        'calendar.previous': 'Précédent',
        'calendar.next': 'Suivant',
        'calendar.settings': 'Paramètres',
        'calendar.viewModeLabel': 'Mode d\'affichage',
        'calendar.dateSourceLabel': 'Source de date',
        'calendar.colorByLabel': 'Couleur des événements',
        'calendar.viewMode.month': 'Mois',
        'calendar.viewMode.week': 'Semaine',
        'calendar.viewMode.agenda': 'Agenda',
        'calendar.dateSource.dueDate': 'Date d\'échéance',
        'calendar.dateSource.startDate': 'Date de début',
        'calendar.dateSource.endDate': 'Date de fin',
        'calendar.dateSource.sprintStart': 'Début du sprint',
        'calendar.dateSource.sprintEnd': 'Fin du sprint',
        'calendar.colorBy.status': 'Statut',
        'calendar.colorBy.priority': 'Priorité',
        'calendar.colorBy.assignee': 'Assigné',
        'calendar.requiresSprint': 'nécessite un champ Sprint',
        'calendar.weekday.0': 'Dim',
        'calendar.weekday.1': 'Lun',
        'calendar.weekday.2': 'Mar',
        'calendar.weekday.3': 'Mer',
        'calendar.weekday.4': 'Jeu',
        'calendar.weekday.5': 'Ven',
        'calendar.weekday.6': 'Sam',
        'calendar.more': 'de plus',
        'calendar.agendaEmptyTitle': 'Aucune tâche à venir',
        'calendar.agendaEmptyBody': 'Les tâches avec une date sur la source active apparaîtront ici.',
        'calendar.settingsTitle': 'Paramètres du calendrier',
        'calendar.settingsHint': 'Choisissez comment la vue Calendrier affiche les tâches sur la grille.',
        'calendar.errorUpdateDate': 'Impossible de déplacer la tâche à la nouvelle date.',
        'calendar.loadError': 'Échec du chargement du calendrier',
        // t18: right-click context menu
        'calendar.contextMenu.createTaskAt': 'Créer une tâche le {date}',
        'calendar.contextMenu.openTask': 'Ouvrir la tâche',
        'calendar.contextMenu.deleteTask': 'Supprimer la tâche',
        // T16 R3: settings-panel filter-rule editor
        'calendar.filterStatusLabel': 'Filtrer par statut',
        'calendar.filterStatusHint': 'Décochez pour masquer les tâches avec ce statut.',
        'calendar.filterClear': 'Tout afficher',
        'calendar.subtasksLabel': 'Sous-tâches',
        'calendar.subtasksEmpty': 'Aucune sous-tâche',
        // T21 R4: "All tasks" date source + Periods section
        'calendar.dateSource.all': 'Toutes les tâches',
        'calendar.periodsLabel': 'Périodes',
        'calendar.periodsHint': 'Plages de dates (ex. absences) affichées en bande derrière la grille.',
        'calendar.periodAdd': 'Ajouter une période',
        'calendar.periodRemove': 'Supprimer la période',
        'calendar.periodClearAll': 'Tout effacer',
        'calendar.periodNamePlaceholder': 'Nom (ex. Absence)',
        'calendar.periodStart': 'Date de début',
        'calendar.periodEnd': 'Date de fin',
        'calendar.periodColor': 'Couleur de la période',
        'gantt.comingSoonTitle': 'Vue Gantt à venir',
        'gantt.comingSoonBody': 'La vue Gantt affichera les tâches sous forme de barres sur une chronologie. En attendant, continuez avec Tableau ou Kanban.',
        'gantt.activeViewLabel': 'Vue active',
        'gantt.previous': 'Précédent',
        'gantt.next': 'Suivant',
        'gantt.settings': 'Paramètres',
        'gantt.timeScaleLabel': 'Échelle de temps',
        'gantt.timeScale.day': 'Jour',
        'gantt.timeScale.week': 'Semaine',
        'gantt.timeScale.month': 'Mois',
        'gantt.timeScale.auto': 'Auto',
        'gantt.groupByLabel': 'Regrouper les lignes par',
        'gantt.groupBy.status': 'Statut',
        'gantt.groupBy.assignee': 'Assigné',
        'gantt.groupBy.none': 'Aucun',
        'gantt.showSprintsLabel': 'Afficher les bandes de sprint',
        'gantt.showSprintsHint': 'Superposez les limites de sprint sur la chronologie.',
        // R3 / t17
        'gantt.showDatelessLabel': 'Afficher les tâches sans date',
        'gantt.showDatelessHint': 'Les tâches sans date sont masquées par défaut — activez cette option pour les afficher en bas de chaque groupe.',
        'gantt.expandSubtasks': 'Déplier les sous-tâches',
        'gantt.collapseSubtasks': 'Replier les sous-tâches',
        // R4 / t22: right-click → cursor-arrow pick-target flow
        'gantt.depMenuSingle': 'Ajouter une dépendance sur cette tâche…',
        'gantt.depPickHint': 'Cliquez une tâche cible pour créer la dépendance. Clic-droit pour annuler.',
        'gantt.depPickCancelHint': 'Sélection annulée',
        'gantt.addDependency': 'Ajouter une dépendance',
        'gantt.removeDependency': 'Supprimer une dépendance',
        'gantt.addDependencyTitle': 'Ajouter une dépendance',
        'gantt.addDependencyDescription': 'Choisissez la direction, la tâche cible et le type de dépendance.',
        'gantt.removeDependencyTitle': 'Supprimer les dépendances',
        'gantt.removeDependencyDescription': 'Supprimez les liens attachés à cette tâche.',
        'gantt.dependencyDirection': 'Direction',
        'gantt.dependencyAsSuccessor': 'Cette tâche en attend une autre',
        'gantt.dependencyAsPredecessor': 'Une autre tâche attend celle-ci',
        'gantt.dependencyTarget': 'Tâche cible',
        'gantt.dependencySearchPlaceholder': 'Rechercher des tâches...',
        'gantt.dependencyType': 'Type de dépendance',
        'gantt.dependencyTypeFS': 'Fin-Début — la tâche B commence après la fin de A.',
        'gantt.dependencyTypeSS': 'Début-Début — la tâche B commence en même temps que A.',
        'gantt.dependencyTypeFF': 'Fin-Fin — la tâche B finit en même temps que A.',
        'gantt.dependencyTypeSF': 'Début-Fin — la tâche B finit au début de A.',
        'gantt.settingsTitle': 'Paramètres du Gantt',
        'gantt.settingsHint': 'Choisissez comment la vue Gantt dispose les tâches sur la chronologie.',
        'gantt.emptyTitle': 'Aucune tâche sur la chronologie',
        'gantt.emptyBody': 'Les tâches avec une date de début et une date de fin apparaîtront ici. Modifiez une tâche pour ajouter des dates.',
        'gantt.errorUpdateDates': 'Impossible de mettre à jour les dates de la tâche.',
        'gantt.loadError': 'Échec du chargement de la vue Gantt',
        'gantt.requiresSprint': 'nécessite un champ Sprint',
        'gantt.bar.handleAria': 'Glisser pour déplacer la tâche',
        'gantt.bar.moveAria': 'Glisser pour déplacer',
        'gantt.bar.resizeEndAria': 'Glisser pour redimensionner la date de fin',
        'gantt.bar.resizeStartAria': 'Glisser pour redimensionner la date de début',
        'gantt.bar.assignedTo': 'Assigné à',
        'gantt.status.TODO': 'À faire',
        'gantt.status.IN_PROGRESS': 'En cours',
        'gantt.status.IN_REVIEW': 'En revue',
        'gantt.status.DONE': 'Terminé',
        'gantt.status.CANCELLED': 'Annulé',
        'gantt.unassigned': 'Non assigné',
        'gantt.filtersLabel': 'Filtres',
        'kanban.subtaskButton': 'Afficher les sous-tâches ({{count}})',
        'kanban.subtaskPopoverTitle': 'Sous-tâches',
        'kanban.subtaskEmpty': 'Aucune sous-tâche.',

        // Kanban settings cog popover (t15)
        'kanban.settings': 'Paramètres du tableau',
        'kanban.settingsTitle': 'Paramètres du tableau',
        'kanban.settingsHint': 'Personnalisez ce qui s\u2019affiche sur le tableau Kanban.',
        'kanban.cardFieldsSection': 'Champs des cartes',
        'kanban.columnVisibilitySection': 'Colonnes',

        // Kanban
        'kanban.cardFieldsTitle': 'Contenu des cartes',
        'kanban.cardFieldsHint': 'Choisissez les champs personnalisés affichés sous forme de pastilles sur chaque carte.',
        'kanban.noCardFieldsAvailable': 'Aucun champ personnalisé affichable.',
        'kanban.columnGrouping': 'Colonnes regroupées par',
        'kanban.columnGroupingHint': 'Le champ dont les valeurs deviennent les colonnes du tableau.',
        'kanban.dropHint': 'Déposez une carte ici',
        'kanban.addColumn': 'Ajouter une colonne',
        'kanban.addColumnTitle': 'Ajouter une colonne',
        'kanban.addColumnDescription': 'Définissez une colonne avec un nom, une couleur et une ou plusieurs règles de filtre. Les tâches qui satisfont toutes les règles atterrissent dans cette colonne.',
        'kanban.columnName': 'Nom de la colonne',
        'kanban.columnNamePlaceholder': 'ex : En revue, Bloqué, Backend',
        'kanban.columnColor': "Couleur d'accent",
        'kanban.columnFilterRules': 'Règles de filtre',
        'kanban.addRule': 'Ajouter une règle',
        'kanban.columns': 'Colonnes',
        'kanban.columnsVisibility': 'Afficher/masquer les colonnes',
        'kanban.columnsVisibilityHint': 'Les colonnes masquées restent dans la vue mais leurs tâches tombent dans "Autre".',
        'kanban.noColumns': "Aucune colonne — ajoutez-en une pour commencer.",
        'kanban.noColumnsTitle': 'Aucune colonne',
        'kanban.noColumnsDescription': "Cette vue Kanban est vide. Ajoutez une première colonne pour commencer à organiser les tâches.",
        'kanban.createFirstColumn': 'Créer la première colonne',
        'kanban.cardFields': 'Champs de carte',
        'kanban.columnCreated': 'Colonne créée',
        'kanban.columnUpdated': 'Colonne mise à jour',
        'kanban.columnDeleted': 'Colonne supprimée',
        'kanban.errorCreateColumn': 'Création de colonne impossible',
        'kanban.errorDeleteColumn': 'Suppression de colonne impossible',
        'kanban.errorUpdateColumn': 'Mise à jour de colonne impossible',
        'kanban.errorReorderColumns': 'Réorganisation des colonnes impossible',
        'kanban.cannotDropHere': 'Déplacement impossible',
        'kanban.cannotDropHereDescription': 'Cette colonne utilise plusieurs règles de filtre. Glissez la carte dans une colonne plus simple (statut).',
        'kanban.editColumnTitle': 'Modifier la colonne',
        'kanban.editColumnDescription': 'Renommez, recolorez ou affinez les règles de filtre de cette colonne.',
        'kanban.contextMenuEdit': 'Modifier la colonne…',
        'kanban.contextMenuHide': 'Masquer la colonne',
        'kanban.contextMenuDelete': 'Supprimer la colonne',
        'kanban.fieldTag.status': 'Statut',
        'kanban.fieldTag.priority': 'Priorité',
        'kanban.fieldTag.assignee': 'Assignée',
        'kanban.fieldTag.customField': 'Champ personnalisé',
        'kanban.fieldTag.other': 'Autre',
        'kanban.operator.eq': 'est',
        'kanban.operator.neq': 'n\u2019est pas',
        'kanban.operator.in': 'fait partie de',
        'kanban.operator.notIn': 'ne fait pas partie de',
        'kanban.operator.isEmpty': 'est vide',
        'kanban.operator.isNotEmpty': 'n\u2019est pas vide',
        'kanban.operator.contains': 'contient',
        'kanban.field.status': 'Statut',
        'kanban.field.priority': 'Priorité',
        'kanban.field.assignee': 'Assignée',
        'kanban.field.customField': 'Champ personnalisé',
        'kanban.statusValue.TODO': 'À faire',
        'kanban.statusValue.IN_PROGRESS': 'En cours',
        'kanban.statusValue.IN_REVIEW': 'En revue',
        'kanban.statusValue.DONE': 'Terminé',
        'kanban.statusValue.CANCELLED': 'Annulé',
        'kanban.priorityValue.NONE': 'Aucune',
        'kanban.priorityValue.LOW': 'Basse',
        'kanban.priorityValue.MEDIUM': 'Moyenne',
        'kanban.priorityValue.HIGH': 'Haute',
        'kanban.priorityValue.URGENT': 'Urgente',

        // Filters
        'filters.filters': 'Filtres',
        'filters.clearFilters': 'Effacer les filtres',
        'filters.clearAll': 'Tout effacer',
        'filters.filterByStatus': 'Filtrer par statut',
        'filters.filterByPriority': 'Filtrer par priorité',
        'filters.filterByAssignee': 'Filtrer par assigné',
        'filters.filterByDueDate': 'Filtrer par date limite',
        'filters.activeFilters': 'Filtres actifs',
        'filters.status': 'Statut',
        'filters.priority': 'Priorité',
        'filters.assignee': 'Assigné',
        'filters.allAssignees': 'Tous les assignés',
        'filters.filter': 'filtre',
        'filters.saveAsView': 'Enregistrer comme vue',
        'filters.saveViewChanges': 'Enregistrer les modifications',
        'filters.addField': 'Ajouter un champ',
        'filters.searchTasks': 'Rechercher des tâches...',
        'filter.operator.contains': 'contient',
        'filter.operator.notContains': 'ne contient pas',
        'filter.operator.equals': 'égal à',
        'filter.operator.notEquals': 'différent de',
        'filter.operator.startsWith': 'commence par',
        'filter.operator.endsWith': 'finit par',
        'filter.operator.lt': '<',
        'filter.operator.lte': '≤',
        'filter.operator.gt': '>',
        'filter.operator.gte': '≥',
        'filter.operator.between': 'entre',
        'filter.operator.before': 'avant',
        'filter.operator.after': 'après',
        'filter.operator.is': 'est',
        'filter.operator.isNot': 'n\'est pas',
        'filter.operator.isAnyOf': 'est l\'un de',
        'filter.operator.isNoneOf': 'n\'est aucun de',
        'filter.operator.containsAll': 'contient tous',
        'filter.operator.containsAny': 'contient l\'un',
        'filter.operator.isEmpty': 'est vide',
        'filter.operator.isNotEmpty': 'n\'est pas vide',
        'filter.operator.isTrue': 'est vrai',
        'filter.operator.isFalse': 'est faux',
        'filter.valuePlaceholder.text': 'Saisir une valeur...',
        'filter.valuePlaceholder.number': 'Nombre',
        'filter.valuePlaceholder.select': 'Sélectionner...',
        'filter.valuePlaceholder.selectMulti': 'Sélectionner des valeurs...',
        'filter.valuePlaceholder.selectPerson': 'Sélectionner une personne...',
        'filter.selected': 'sélectionnés',
        'filter.addFilter': 'Filtrer',
        'filter.filterByField': 'Filtrer par champ',
        'filter.group.text': 'Texte',
        'filter.group.number': 'Nombres',
        'filter.group.date': 'Dates',
        'filter.group.category': 'Catégories',
        'filter.group.person': 'Personnes',

        // Comments
        'comments.addComment': 'Ajouter un commentaire',
        'comments.editComment': 'Modifier le commentaire',
        'comments.deleteComment': 'Supprimer le commentaire',
        'comments.deleteTitle': 'Supprimer le commentaire',
        'comments.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer ce commentaire ? Cette action est irréversible.',
        'comments.noComments': 'Aucun commentaire pour le moment',
        'comments.beFirstToComment': 'Soyez le premier à commenter !',
        'comments.commentPlaceholder': 'Écrire un commentaire...',
        'comments.editPlaceholder': 'Modifier votre commentaire...',
        'comments.edited': 'modifié',
        'comments.submitHint': 'Appuyez sur ⌘+Entrée pour envoyer',
        'comments.mentionCount': 'mentionné(s)',
        'comments.mentionHint': 'Tapez @ pour mentionner quelqu\'un',
        'comments.isTyping': 'écrit...',
        'comments.areTyping': 'écrivent...',
        'comments.andOthersTyping': 'et {count} autres écrivent...',
        'comments.save': 'Enregistrer',
        'comments.comment': 'Commenter',
        'comments.failedToLoad': 'Échec du chargement des commentaires',

        // Attachments
        'attachments.addAttachment': 'Ajouter une pièce jointe',
        'attachments.uploadFile': 'Téléverser un fichier',
        'attachments.upload': 'Téléverser',
        'attachments.noAttachments': 'Aucune pièce jointe',
        'attachments.noAttachmentsHint': 'Cliquez sur téléverser pour ajouter des fichiers.',
        'attachments.download': 'Télécharger',
        'attachments.deleteAttachment': 'Supprimer la pièce jointe',
        'attachments.deleteTitle': 'Supprimer la pièce jointe',
        'attachments.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer cette pièce jointe ? Cette action est irréversible.',
        'attachments.uploaded': 'téléversé',
        'attachments.failedToLoad': 'Échec du chargement des pièces jointes',
        'attachments.fileTooLarge': 'La taille du fichier doit être inférieure à 50 Mo',
        'attachments.openInNewTab': 'Ouvrir dans un nouvel onglet',

        // Notifications
        'notifications.title': 'Notifications',
        'notifications.markAllRead': 'Tout marquer comme lu',
        'notifications.noNotifications': 'Aucune notification',
        'notifications.viewInvitation': 'Voir l\'invitation',
        'notifications.taskAssigned': 'Tâche assignée',
        'notifications.taskAssignedDesc': 'Lorsqu\'une tâche vous est assignée',
        'notifications.taskUpdated': 'Tâche mise à jour',
        'notifications.taskUpdatedDesc': 'Lorsqu\'une tâche qui vous est assignée est modifiée',
        'notifications.commentAdded': 'Nouveau commentaire',
        'notifications.commentAddedDesc': 'Lorsqu\'un commentaire est ajouté à vos tâches',
        'notifications.attachmentAdded': 'Nouvelle pièce jointe',
        'notifications.attachmentAddedDesc': 'Lorsqu\'une pièce jointe est ajoutée à vos tâches',
        'notifications.mentioned': 'Mentions',
        'notifications.mentionedDesc': 'Lorsque vous êtes mentionné dans un commentaire',
        'notifications.projectInvite': 'Invitations aux projets',
        'notifications.projectInviteDesc': 'Lorsque vous êtes invité à un projet',
        'notifications.dueDateReminder': "Rappels d'échéance",
        'notifications.dueDateReminderDesc': 'Rappels avant l\'échéance des tâches',
        'notifications.taskOverdue': 'Tâches en retard',
        'notifications.taskOverdueDesc': 'Lorsqu\'une tâche dépasse sa date d\'échéance',

        // Settings
        'settings.title': 'Paramètres',
        'settings.profile': 'Profil',
        'settings.profileInfo': 'Informations du profil',
        'settings.profilePicture': 'Photo de profil',
        'settings.uploadPicture': 'Téléverser une photo',
        'settings.removePicture': 'Retirer',
        'settings.changePassword': 'Changer le mot de passe',
        'settings.updatePassword': 'Mettre à jour le mot de passe',
        'settings.language': 'Langue',
        'settings.language.title': 'Langue',
        'settings.language.description': 'Choisissez votre langue préférée pour l\'interface',
        'settings.language.select': 'Sélectionner la langue',
        'settings.selectLanguage': 'Sélectionner la langue',
        'settings.notificationSettings': 'Paramètres de notification',
        'settings.discordIntegration': 'Intégration Discord',
        'settings.discordIntegrationDesc': 'Recevez des notifications via Discord',
        'settings.connectDiscord': 'Connecter Discord',
        'settings.disconnectDiscord': 'Déconnecter',
        'settings.discordConnected': 'Connecté',
        'settings.discordReady': 'Notifications Discord opérationnelles',
        'settings.discordReadyDescription': 'Le message privé de test a bien été livré.',
        'settings.discordNeedsReconnect': 'Discord doit être reconnecté',
        'settings.reconnectDiscord': 'Reconnecter',
        'settings.discordLinkStepInstall': 'Installez Project Manager sur votre compte utilisateur Discord.',
        'settings.discordLinkStepCommand': 'Dans n’importe quel serveur ou conversation, lancez /link avec ce code. La réponse reste privée.',
        'settings.discordCommandHint': 'Dans n’importe quel champ de texte Discord, saisissez : /link code:{code}',
        'settings.installDiscordApp': 'Installer / ouvrir Discord',
        'settings.copyCode': 'Copier le code',
        'settings.codeCopied': 'Code de connexion Discord copié',
        'settings.inAppNotifications': 'Notifications dans l\'app',
        'settings.discordNotifications': 'Notifications Discord',
        'settings.emailFallbackNotifications': 'Secours par e-mail',
        'settings.emailFallbackDescription': 'Uniquement si Discord n’est pas livré',
        'settings.reminderTiming': 'Délai des rappels d\'échéance',
        'settings.reminderTimingDesc': 'Quand recevoir les rappels d\'échéance',
        'settings.before1h': '1 heure avant',
        'settings.before24h': '24 heures avant',
        'settings.before48h': '48 heures avant',
        'settings.after24h': '24 heures après retard',
        'settings.after48h': '48 heures après retard',
        // Theme settings
        'settings.theme': 'Thème',
        'settings.themeTitle': 'Apparence',
        'settings.themeDescription': 'Personnalisez l\'apparence de l\'application',
        'settings.interactiveEffects': 'Effets interactifs',
        'settings.interactiveEffectsDesc': 'Activer les particules animées et les interactions avec le curseur',

        // Dashboard
        'dashboard.welcome': 'Bienvenue',
        'dashboard.assignedTasks': 'Tâches assignées',
        'dashboard.dueToday': "À faire aujourd'hui",
        'dashboard.completedThisWeek': 'Terminées cette semaine',
        'dashboard.recentTasks': 'Tâches récentes',
        'dashboard.quickActions': 'Actions rapides',
        'dashboard.createNewProject': 'Créer un nouveau projet',

        // Errors
        'error.somethingWentWrong': "Une erreur s'est produite",
        'error.notFound': 'Non trouvé',
        'error.unauthorized': 'Non autorisé',
        'error.forbidden': 'Accès refusé',
        'error.networkError': 'Erreur réseau',
        'error.tryAgain': 'Réessayer',
        'error.projectNotFoundDesc': "Le projet que vous recherchez n'existe pas ou vous n'y avez pas accès.",

        // Success messages
        'success.saved': 'Enregistré avec succès',
        'success.deleted': 'Supprimé avec succès',
        'success.created': 'Créé avec succès',
        'success.updated': 'Mis à jour avec succès',
        'success.copied': 'Copié dans le presse-papiers',

        // Confirmation dialogs
        'confirm.deleteTask': 'Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible. Toutes les sous-tâches seront également supprimées.',
        'confirm.deleteProject': 'Êtes-vous sûr de vouloir supprimer ce projet ?',
        'confirm.deleteComment': 'Êtes-vous sûr de vouloir supprimer ce commentaire ?',
        'confirm.deleteAttachment': 'Êtes-vous sûr de vouloir supprimer cette pièce jointe ?',
        'confirm.deleteView': 'Êtes-vous sûr de vouloir supprimer cette vue ?',
        'confirm.leaveProject': 'Êtes-vous sûr de vouloir quitter ce projet ?',
        'confirm.cannotUndo': 'Cette action est irréversible.',
        'confirm.deleteCustomField': 'Êtes-vous sûr de vouloir supprimer ce champ personnalisé ? Cela supprimera le champ et ses valeurs de toutes les tâches.',

        // Custom fields
        'customFields.title': 'Champs personnalisés',
        'customFields.addField': 'Ajouter un champ',
        'customFields.editField': 'Modifier le champ',
        'customFields.deleteField': 'Supprimer le champ',
        'customFields.fieldName': 'Nom du champ',
        'customFields.fieldType': 'Type de champ',
        'customFields.required': 'Requis',
        'customFields.options': 'Options',
        'customFields.addOption': 'Ajouter une option',
        'customFields.text': 'Texte',
        'customFields.number': 'Nombre',
        'customFields.date': 'Date',
        'customFields.select': 'Sélection',
        'customFields.multiSelect': 'Sélection multiple',
        'customFields.checkbox': 'Case à cocher',
        'customFields.url': 'URL',
        'customFields.email': 'E-mail',
        'customFields.person': 'Personne',
        'customFields.sprint': 'Sprint',
        'customFields.sprintOptionName': 'Nom du sprint',
        'customFields.sprintOptionStart': 'Date de début',
        'customFields.sprintOptionEnd': 'Date de fin',
        'customFields.sprintAddOption': 'Ajouter un sprint',
        'customFields.sprintNoOptions': 'Aucun sprint. Ajoutez-en un pour commencer.',

        // Project Settings
        'projectSettings.projectDetails': 'Détails du projet',
        'projectSettings.projectName': 'Nom du projet',
        'projectSettings.description': 'Description',
        'projectSettings.projectIcon': 'Icône du projet',
        'projectSettings.emoji': 'Emoji',
        'projectSettings.image': 'Image',
        'projectSettings.chooseEmoji': 'Choisir un emoji',
        'projectSettings.uploadImage': 'Télécharger une image',
        'projectSettings.maxSize': 'Taille max : 5 Mo. Formats supportés : PNG, JPG, GIF, WebP',
        'projectSettings.color': 'Couleur',
        'projectSettings.processingImage': 'Traitement de l\'image...',
        'projectSettings.saveChanges': 'Enregistrer les modifications',
        'projectSettings.dangerZone': 'Zone de danger',
        'projectSettings.dangerZoneDescription': 'Une fois le projet supprimé, il n\'y a pas de retour en arrière. Toutes les tâches, commentaires et pièces jointes seront définitivement supprimés.',
        'projectSettings.teamMembers': 'Membres de l\'équipe',
        'projectSettings.pendingInvitations': 'Invitations en attente',
        'projectSettings.inviteTeamMember': 'Inviter un membre',
        'projectSettings.inviteDescription': 'Envoyez une invitation pour rejoindre ce projet. Il recevra un e-mail avec un lien pour accepter.',
        'projectSettings.joinLinkTitle': 'Inviter avec un lien',
        'projectSettings.joinLinkDescription': 'Toute personne disposant de ce lien peut rejoindre le projet comme membre.',
        'projectSettings.createJoinLink': 'Créer un lien d’invitation',
        'projectSettings.regenerateJoinLink': 'Générer un nouveau lien',
        'projectSettings.disableJoinLink': 'Désactiver le lien',
        'projectSettings.joinLinkWarning': 'La génération d’un nouveau lien invalide immédiatement le précédent.',
        'projectSettings.integrations': 'Intégrations',
        'projectSettings.discordWebhookTitle': 'Salon Discord du projet',
        'projectSettings.discordWebhookDescription': 'Publiez l’activité de l’équipe dans un salon Discord grâce à un webhook entrant.',
        'projectSettings.discordWebhookUrl': 'URL du webhook Discord',
        'projectSettings.discordWebhookHint': 'Dans Discord : Paramètres du salon → Intégrations → Webhooks → Nouveau webhook, puis copiez son URL.',
        'projectSettings.connectWebhook': 'Enregistrer et tester',
        'projectSettings.testWebhook': 'Envoyer un test',
        'projectSettings.disconnectWebhook': 'Déconnecter',
        'projectSettings.webhookConnected': 'Salon Discord connecté',
        'projectSettings.webhookChannel': 'Identifiant du salon',
        'projectSettings.webhookSaved': 'Le webhook Discord est enregistré et le message de test a été livré.',
        'projectSettings.webhookRemoved': 'Le webhook Discord a été déconnecté.',
        'projectSettings.webhookTestSent': 'Le message de test a bien été livré sur Discord.',
        'projectSettings.data': 'Import / Export',
        'projectSettings.transferTitle': 'Import et export des tâches',
        'projectSettings.transferDescription': 'Déplacez ou modifiez en masse les tâches avec un fichier JSON versionné.',
        'projectSettings.exportTitle': 'Exporter les tâches du projet',
        'projectSettings.exportDescription': 'Télécharge les champs personnalisés, leurs valeurs modifiables, les assignés et toute l’arborescence des sous-tâches.',
        'projectSettings.exportButton': 'Exporter en JSON',
        'projectSettings.exporting': 'Export en cours...',
        'projectSettings.importTitle': 'Importer ou mettre à jour des tâches',
        'projectSettings.importDescription': 'Les identifiants existants mettent à jour les tâches ; les nouveaux sont créés. Les champs et options manquants sont ajoutés automatiquement. Les tâches marquées « deleted » (ou listées dans « deletedTasks ») sont supprimées.',
        'projectSettings.importButton': 'Choisir un fichier JSON',
        'projectSettings.previewingImport': 'Vérification du fichier...',
        'projectSettings.importSafetyHint': 'Le fichier est vérifié avant l’import. Les tâches absentes du fichier ne sont jamais supprimées : seules les suppressions explicites (« deleted »: true ou « deletedTasks ») effacent une tâche, avec ses sous-tâches. L’import est appliqué atomiquement.',
        'projectSettings.importPreviewTitle': 'Confirmer l’import des tâches',
        'projectSettings.importPreviewDescription': 'Vérifiez les changements de {file} avant de les appliquer à ce projet.',
        'projectSettings.tasksToCreate': 'Tâches à créer',
        'projectSettings.tasksToUpdate': 'Tâches à mettre à jour',
        'projectSettings.tasksToDelete': 'Tâches à supprimer',
        'projectSettings.fieldsToCreate': 'Champs à créer',
        'projectSettings.fieldsToUpdate': 'Champs à mettre à jour',
        'projectSettings.optionsToAdd': 'Options à ajouter',
        'projectSettings.importWarnings': 'Avertissements',
        'projectSettings.confirmImport': 'Importer les changements',
        'projectSettings.importing': 'Import en cours...',
        'projectSettings.importSuccess': 'Import terminé',
        'projectSettings.importSuccessDescription': '{created} tâche(s) créée(s), {updated} mise(s) à jour et {deleted} supprimée(s).',
        'projectSettings.invalidImportFile': 'Ce fichier JSON n’est pas un export de tâches Project Manager valide.',
        'projectSettings.importFileTooLarge': 'Le fichier d’import doit faire moins de 25 Mo.',
        'projectSettings.emailAddress': 'Adresse e-mail',
        'projectSettings.emailPlaceholder': 'collegue@exemple.com',
        'projectSettings.role': 'Rôle',
        'projectSettings.roleAdminDesc': 'Les admins peuvent gérer les membres et les paramètres du projet.',
        'projectSettings.roleMemberDesc': 'Les membres peuvent créer et modifier des tâches, commenter et télécharger des fichiers.',
        'projectSettings.roleViewerDesc': 'Les lecteurs ont un accès en lecture seule au projet.',
        'projectSettings.sendInvitation': 'Envoyer l\'invitation',
        'projectSettings.copyLink': 'Copier le lien',
        'projectSettings.youIndicator': '(vous)',
        'projectSettings.projectNotFound': 'Projet non trouvé',
        'projectSettings.backToProjects': 'Retour aux projets',
        'projectSettings.deleteProjectTitle': 'Supprimer le projet',
        'projectSettings.deleteProjectDescription': 'Êtes-vous sûr de vouloir supprimer "{name}" ? Cette action est irréversible. Toutes les tâches, commentaires et pièces jointes seront définitivement supprimés.',

        // Invite Links (R14 t46)
        'inviteLink.sectionTitle': "Liens d'invitation",
        'inviteLink.sectionDescription': "Créez des liens d'invitation authentifiés. Chaque lien ajoute un utilisateur comme Membre ou Lecteur ; révocable à tout moment.",
        'inviteLink.create': 'Créer un lien',
        'inviteLink.createFirst': 'Créer le premier lien',
        'inviteLink.createTitle': "Créer un lien d'invitation",
        'inviteLink.createDescription': "Choisissez le rôle et une expiration optionnelle. Une connexion est requise pour l'utiliser.",
        'inviteLink.role': 'Rôle',
        'inviteLink.role.MEMBER': 'Membre',
        'inviteLink.role.MEMBER.description': 'Peut créer et modifier des tâches, commenter, téléverser des fichiers.',
        'inviteLink.role.VIEWER': 'Lecteur',
        'inviteLink.role.VIEWER.description': 'Accès en lecture seule aux tâches, vues et commentaires.',
        'inviteLink.hasExpiry': "Définir une date d'expiration",
        'inviteLink.expiresAt': 'Expire le {date}',
        'inviteLink.empty': "Aucun lien d'invitation pour l'instant.",
        'inviteLink.copy': 'Copier',
        'inviteLink.copied': 'Copié',
        'inviteLink.copyFailed': 'Impossible de copier dans le presse-papiers.',
        'inviteLink.revoke': 'Révoquer',
        'inviteLink.revoked': "Lien d'invitation révoqué.",
        'inviteLink.created': "Lien d'invitation créé.",
        'inviteLink.createdBy': 'Créé par {name} le {date}',
        'inviteLink.status.active': 'Actif',
        'inviteLink.status.revoked': 'Révoqué',
        'inviteLink.status.expired': 'Expiré',
        'inviteLink.title': 'Vous êtes invité',
        'inviteLink.subtitle': 'Vous êtes invité à rejoindre « {name} »',
        'inviteLink.invitedBy': 'Invité par {name}',
        'inviteLink.accept': "Accepter l'invitation",
        'inviteLink.decline': 'Plus tard',
        'inviteLink.authenticatedNotice': 'Connexion requise pour accepter.',
        'inviteLink.acceptSuccess': 'Bienvenue !',
        'inviteLink.acceptSuccessDescription': 'Vous avez rejoint {name}.',
        'inviteLink.acceptFailed': "Impossible d'utiliser le lien d'invitation.",
        'inviteLink.invalid': "Lien d'invitation invalide",
        'inviteLink.invalidDescription': 'Ce lien a été révoqué, expiré ou n\'a jamais existé.',
        'inviteLink.backToProjects': 'Retour aux projets',

        // Toast messages
        'toast.invalidFileType': 'Type de fichier invalide',
        'toast.fileTooLarge': 'Fichier trop volumineux',
        'toast.uploadFailed': 'Échec du téléchargement',
        'toast.settingsSaved': 'Paramètres enregistrés',
        'toast.settingsSavedDesc': 'Les paramètres du projet ont été mis à jour.',
        'toast.invitationSent': 'Invitation envoyée',
        'toast.invitationSentDesc': 'Une invitation a été créée pour {email}.',
        'toast.projectDeleted': 'Projet supprimé',
        'toast.projectDeletedDesc': 'Le projet a été définitivement supprimé.',
        'toast.roleUpdated': 'Rôle mis à jour',
        'toast.roleUpdatedDesc': 'Le rôle du membre a été mis à jour.',
        'toast.memberRemoved': 'Membre retiré',
        'toast.memberRemovedDesc': 'Le membre a été retiré du projet.',
        'toast.invitationCancelled': 'Invitation annulée',
        'toast.invitationCancelledDesc': 'L\'invitation a été annulée.',
        'toast.linkCopied': 'Lien copié',
        'toast.linkCopiedDesc': 'Le lien d\'invitation a été copié dans le presse-papiers.',

        // Table columns
        'table.selection': 'Sélection',
        'table.task': 'Tâche',
        'table.actions': 'Actions',
        'table.horizontalScroll': 'Faire défiler les champs horizontalement',
        'tasks.paginationShowing': 'Affichage de',
        'tasks.paginationOf': 'sur',
        'tasks.loadMore': 'Charger plus de tâches',

        // Task Dialog
        'taskDialog.createTitle': 'Créer une nouvelle tâche',
        'taskDialog.editTitle': 'Modifier la tâche',
        'taskDialog.createDescription': 'Remplissez les détails pour créer une nouvelle tâche.',
        'taskDialog.editDescription': 'Modifiez les détails de la tâche ci-dessous.',
        'taskDialog.title': 'Titre',
        'taskDialog.titleRequired': 'Le titre de la tâche est requis',
        'taskDialog.titlePlaceholder': 'Entrez le titre de la tâche...',
        'taskDialog.description': 'Description',
        'taskDialog.descriptionPlaceholder': 'Ajouter une description...',
        'taskDialog.selectStatus': 'Sélectionner le statut',
        'taskDialog.selectPriority': 'Sélectionner la priorité',
        'taskDialog.noTeamMembers': 'Aucun membre d\'équipe disponible',
        'taskDialog.saveChanges': 'Enregistrer les modifications',
        'taskDialog.createTask': 'Créer la tâche',
        'taskDialog.owner': 'Propriétaire',
        'taskDialog.customFields': 'Champs personnalisés',
        'taskDialog.selectPlaceholder': 'Sélectionner...',

        // Create Project Dialog
        'createProject.title': 'Créer un nouveau projet',
        'createProject.description': 'Créez un nouveau projet pour organiser vos tâches et collaborer avec votre équipe.',
        'createProject.projectName': 'Nom du projet',
        'createProject.projectNamePlaceholder': 'Entrez le nom du projet',
        'createProject.projectDescription': 'Description',
        'createProject.projectDescriptionPlaceholder': 'Décrivez le but de ce projet',
        'createProject.descriptionOptional': '(optionnel)',
        'createProject.projectIcon': 'Icône du projet',
        'createProject.emoji': 'Emoji',
        'createProject.image': 'Image',
        'createProject.chooseEmoji': 'Choisir un emoji',
        'createProject.uploadImage': 'Télécharger une image',
        'createProject.maxSizeHint': 'Taille max: 5 Mo. Formats supportés: PNG, JPG, GIF, WebP',
        'createProject.color': 'Couleur',
        'createProject.preview': 'Aperçu',
        'createProject.processingImage': 'Traitement de l\'image...',
        'createProject.createButton': 'Créer le projet',
        'createProject.newProject': 'Nouveau projet',
        'createProject.success': 'Projet créé',
        'createProject.successDesc': '"{name}" a été créé avec succès.',
        'createProject.uploadFailedDesc': 'Échec du traitement de l\'image. Le projet sera créé sans image.',

        // Project Card Actions
        'projectCard.settings': 'Paramètres',
        'projectCard.archive': 'Archiver',
        'projectCard.unarchive': 'Désarchiver',
        'projectCard.delete': 'Supprimer',
        'projectCard.leaveProject': 'Quitter le projet',
        'projectCard.archived': 'Archivé',
        'projectCard.tasks': 'tâches',

        // Settings page additional
        'settings.emailCannotBeChanged': 'L\'e-mail ne peut pas être modifié',
        'settings.chooseNotifications': 'Choisissez les notifications que vous souhaitez recevoir',
        'settings.linkDiscordDesc': 'Liez votre Discord pour recevoir des notifications',
        'settings.uploadProfilePictureHint': 'Téléversez une photo de profil (max 5 Mo)',

        // Toast messages for tasks
        'toast.taskCreated': 'Tâche créée',
        'toast.taskCreatedDesc': 'La tâche a été créée avec succès.',
        'toast.taskUpdated': 'Tâche mise à jour',
        'toast.taskUpdatedDesc': 'La tâche a été mise à jour avec succès.',
        'toast.taskDeleted': 'Tâche supprimée',
        'toast.taskDeletedDesc': 'La tâche a été supprimée avec succès.',
        'toast.tasksDeleted': 'Tâches supprimées',
        'toast.tasksDeletedDesc': '{count} tâche(s) supprimée(s) avec succès.',
        'toast.statusUpdated': 'Statut mis à jour',
        'toast.statusUpdatedDesc': '{count} tâche(s) mise(s) à jour.',
        'toast.priorityUpdated': 'Priorité mise à jour',
        'toast.priorityUpdatedDesc': '{count} tâche(s) mise(s) à jour.',
        'toast.assigneesUpdated': 'Assignés mis à jour',
        'toast.assigneesUpdatedDesc': 'Assigné ajouté à {count} tâche(s).',
        'toast.viewSaved': 'Vue enregistrée',
        'toast.viewSavedDesc': 'Vos modifications de vue ont été enregistrées.',
        'toast.viewDeleted': 'Vue supprimée',
        'toast.errorSavingView': 'Échec de l\'enregistrement des modifications de vue.',
        'toast.errorDeletingTasks': 'Échec de la suppression de certaines tâches.',
        'toast.errorUpdatingTasks': 'Échec de la mise à jour de certaines tâches.',
        'toast.subtaskCreated': 'Sous-tâche créée',
        'toast.subtaskCreatedDesc': 'Une nouvelle sous-tâche a été ajoutée.',
        'toast.dueDateUpdated': 'Date d\'échéance mise à jour',
        'toast.dueDateSet': 'La date d\'échéance a été définie.',
        'toast.dueDateCleared': 'La date d\'échéance a été effacée.',
        'toast.fieldDeleted': 'Champ supprimé',
        'toast.fieldDeletedDesc': 'Le champ personnalisé a été supprimé.',
        'toast.errorDeletingField': 'Échec de la suppression du champ personnalisé.',
        'toast.errorAssignees': 'Échec de la mise à jour des assignés.',
        'toast.errorDueDate': 'Échec de la mise à jour de la date d\'échéance.',
        'toast.errorCustomField': 'Échec de la mise à jour du champ personnalisé.',
        'toast.errorTaskAction': 'Échec de {action} la tâche. Veuillez réessayer.',

        // Custom field dialog
        'customFields.editFieldTitle': 'Modifier le champ',
        'customFields.addFieldTitle': 'Ajouter un champ personnalisé',
        'customFields.editFieldDescription': 'Mettre à jour les paramètres du champ personnalisé.',
        'customFields.addFieldDescription': 'Créer un nouveau champ personnalisé pour vos tâches.',
        'customFields.fieldNamePlaceholder': 'ex: Catégorie, Sprint, etc.',
        'customFields.saving': 'Enregistrement...',
        'customFields.createField': 'Créer le champ',
        'customFields.noFieldsYet': 'Aucun champ personnalisé.',
        'customFields.noFieldsHint': 'Ajoutez-en un pour commencer.',
        'customFields.optionsCount': '{count} options',
        'customFields.deleteFieldTitle': 'Supprimer le champ personnalisé',
        'customFields.deleteFieldDescription': 'Êtes-vous sûr de vouloir supprimer "{name}" ? Cela supprimera le champ et ses valeurs de toutes les tâches de ce projet.',
        'customFields.deleting': 'Suppression...',

        // Saved views panel
        'views.deleteViewTitle': 'Supprimer la vue',
        'views.deleteViewConfirmation': 'Êtes-vous sûr de vouloir supprimer cette vue ? Cette action est irréversible.',

        // Landing page
        'landing.heroTitle': 'Gestion de projet,',
        'landing.heroSubtitle': 'réinventée',
        'landing.getStarted': 'Commencer',
        'landing.tryDemo': 'Essayer la démo',
        'landing.feature.projects': 'Organiser les projets',
        'landing.feature.projectsDesc': 'Créez et gérez des projets illimités avec des icônes, couleurs et permissions personnalisées.',
        'landing.feature.team': 'Collaboration d\'équipe',
        'landing.feature.teamDesc': 'Invitez des membres, attribuez des rôles et travaillez ensemble sans effort.',
        'landing.feature.realtime': 'Mises à jour en temps réel',
        'landing.feature.realtimeDesc': 'Voyez les changements instantanément. Aucun rafraîchissement nécessaire.',
        'landing.feature.comments': 'Commentaires & pièces jointes',
        'landing.feature.commentsDesc': 'Discutez des tâches avec @mentions et joignez des fichiers directement.',
        'landing.feature.views': 'Vues personnalisées',
        'landing.feature.viewsDesc': 'Enregistrez des vues avec vos propres filtres et personnalisez les colonnes, vous permettant d\'organiser votre espace de travail selon vos préférences.',
        'landing.feature.themes': 'Plusieurs thèmes',
        'landing.feature.themesDesc': 'Choisissez parmi des thèmes animés avec des effets de particules interactifs.',
        'landing.feature.notifications': 'Notifications intelligentes',
        'landing.feature.notificationsDesc': 'Restez informé avec les notifications in-app et Discord.',
        'landing.feature.customFields': 'Colonnes personnalisées',
        'landing.feature.customFieldsDesc': 'Ajoutez du texte, des nombres, des dates, des listes déroulantes et plus encore.',
        'landing.demo.title': 'Découvrez par vous-même',
        'landing.demo.subtitle': 'Essayez de changer de thème et interagissez avec la démo ci-dessous',
        'landing.demo.tryTheme': 'Choisir un thème',
        'landing.demo.taskDemo': 'Démo interactive des tâches',
        'landing.demo.interactiveDemo': 'Cliquez sur les tâches, changez le statut et explorez !',
        'landing.cta.title': 'Prêt à vous organiser ?',
        'landing.cta.subtitle': 'Commencez à gérer vos projets avec style dès aujourd\'hui.',
        'landing.scrollToExplore': 'Défilez pour explorer',
        'landing.builtWith': 'Construit avec',
        'landing.selfHosted': 'Gratuit pour toujours',
        'landing.selfHostedDesc': 'Pas de carte de crédit requise. Jamais.',
        'landing.openSource': 'Open source',
        'landing.freeForAll': 'Gratuit pour tous',
        'landing.freeForAllDesc': 'Pas de frais cachés, pas de limites. Toutes les fonctionnalités gratuitement.',
        'landing.hostedForYou': 'Hébergé pour vous',
        'landing.hostedForYouDesc': 'Nous gérons les serveurs, vous vous concentrez sur vos projets.',
    },
};

// ============================================
// CONTEXT
// ============================================

interface I18nContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: keyof Translations) => string;
    initializeLanguage: (serverLanguage?: string | null) => void;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

// ============================================
// GEOLOCATION DETECTION
// ============================================

async function detectUserCountry(): Promise<string | null> {
    try {
        // Use a free geo-IP service to detect user's country
        const response = await fetch('https://ipapi.co/json/', {
            signal: AbortSignal.timeout(3000) // 3 second timeout
        });
        if (response.ok) {
            const data = await response.json() as { country_code?: string };
            return data.country_code || null;
        }
    } catch {
        // Silently fail - we'll fall back to browser language
    }
    return null;
}

// ============================================
// PROVIDER
// ============================================

const LANGUAGE_STORAGE_KEY = 'app-language';
const COUNTRY_DETECTED_KEY = 'country-detected';

export function I18nProvider({ children }: { children: React.ReactNode }) {
    const [language, setLanguageState] = useState<Language>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
            if (saved && (saved === 'en' || saved === 'fr')) {
                return saved;
            }
            // Try to detect browser language - default to French if browser is French
            const browserLang = navigator.language.toLowerCase();
            if (browserLang.startsWith('fr')) {
                return 'fr';
            }
        }
        // Default to French
        return 'fr';
    });

    // Detect country on first visit
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const hasDetected = localStorage.getItem(COUNTRY_DETECTED_KEY);
            const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);

            // Only detect country if we haven't before and user hasn't explicitly chosen a language
            if (!hasDetected && !savedLanguage) {
                detectUserCountry().then((countryCode) => {
                    localStorage.setItem(COUNTRY_DETECTED_KEY, 'true');

                    // French-speaking countries
                    const frenchCountries = ['FR', 'BE', 'CH', 'CA', 'LU', 'MC', 'SN', 'CI', 'ML', 'BF', 'NE', 'TG', 'BJ', 'GA', 'CG', 'CD', 'CM', 'MG', 'HT'];

                    if (countryCode && frenchCountries.includes(countryCode)) {
                        setLanguageState('fr');
                        localStorage.setItem(LANGUAGE_STORAGE_KEY, 'fr');
                    } else if (countryCode) {
                        // Keep default (French), but if they're definitely not French-speaking, could set English
                        // For now, we keep French as requested
                    }
                });
            }
        }
    }, []);

    // Initialize language from server (called after login/auth check)
    const initializeLanguage = useCallback((serverLanguage?: string | null) => {
        if (serverLanguage && (serverLanguage === 'en' || serverLanguage === 'fr')) {
            setLanguageState(serverLanguage);
            if (typeof window !== 'undefined') {
                localStorage.setItem(LANGUAGE_STORAGE_KEY, serverLanguage);
            }
        }
    }, []);

    const setLanguage = useCallback(async (lang: Language) => {
        setLanguageState(lang);
        if (typeof window !== 'undefined') {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        }
        // Update language on the server — use relative /api so nginx can
        // proxy to the backend (no localhost:8000 fallback).
        try {
            await fetch(`/api/auth/profile`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ language: lang }),
            });
        } catch (error) {
            console.error('Failed to save language preference to server:', error);
        }
    }, []);

    const t = useCallback(
        (key: keyof Translations): string => {
            return translations[language][key] || key;
        },
        [language]
    );

    return (
        <I18nContext.Provider value={{ language, setLanguage, t, initializeLanguage }}>
            {children}
        </I18nContext.Provider>
    );
}

// ============================================
// HOOK
// ============================================

export function useI18n() {
    const context = useContext(I18nContext);
    if (!context) {
        throw new Error('useI18n must be used within an I18nProvider');
    }
    return context;
}

// Export language options for settings
export const languageOptions: { value: Language; label: string; flag: string }[] = [
    { value: 'en', label: 'English', flag: '🇺🇸' },
    { value: 'fr', label: 'Français', flag: '🇫🇷' },
];
